/**
 * GameAbilities — Concrete ability definitions and executors for Project_X.
 *
 * Each ability is defined with:
 * - A stable ID (used in minion definitions and upgrade branches)
 * - A human-readable name and description
 * - A trigger type (OnDraw, OnAttack, OnKill, OnCombatStart, OnDeath)
 * - An executor function that implements the ability's effect
 *
 * These abilities are registered with the AbilityDispatcher at server startup.
 */

import { AbilityDefinition, AbilityTriggerType } from '../shared/models/AbilityRegistry';
import {
  AbilityExecutionContext,
  AbilityExecutor,
  OnAttackEventData,
  OnKillEventData,
  OnDeathEventData,
} from '../shared/models/AbilityExecutionContext';
import { CombatMinionCopy } from '../shared/models/CombatCopy';
import { PlayerState } from '../shared/models/Player';
import {
  RUNE_BOOM_RESISTANT_ID,
  RUNE_HEALING_ID,
  RUNE_HEALING_MULTIPLIER,
  RUNE_OVERFEED_ID,
} from '../shared/models/RuneDefinitions';
import {
  BLAST_ABILITY_ID,
  BLAST_SPLASH_FRACTION,
  FREEZE_ABILITY_ID,
  FREEZE_TARGET_CHANCE,
  FREEZE_NEIGHBOR_CHANCE,
  BUFF_ATTACK_ABILITY_ID,
  BUFF_ATTACK_BONUS,
  DISCOUNT_ABILITY_ID,
  DISCOUNT_COST_REDUCTION,
  FORTRESS_ABILITY_ID,
  FORTRESS_HP_BONUS,
  BLESSED_ABILITY_ID,
  DEATHFEEDER_ABILITY_ID,
  DEATHFEEDER_FEED_STACKS,
  EXPLODING_ABILITY_ID,
  EXPLODING_DAMAGE,
  MASTER_BUFF_ABILITY_ID,
  MASTER_BUFF_STAT_BONUS,
  SOULLINK_ABILITY_ID,
  WARTRIBE_ABILITY_ID,
  WARTRIBE_ATK_BONUS,
  HOLY_SHIELD_ABILITY_ID,
  SECOND_CHANCE_ABILITY_ID,
  RUNE_KEEPER_ABILITY_ID,
  RUNE_KEEPER_CAP_BONUS,
  RUNE_FEAST_ABILITY_ID,
  RUNE_FEAST_DISCOUNT_FRACTION,
  TOXIC_CLOUD_ABILITY_ID,
  TOXIC_CLOUD_DAMAGE,
  BLUNT_ABILITY_ID,
  BLUNT_ATK_REDUCTION,
  MARK_ABILITY_ID,
  MARK_EXTRA_DAMAGE,
  SHARED_PAIN_ABILITY_ID,
  FRENZY_ABILITY_ID,
  FRENZY_ATK_BONUS,
  RANDOM_EVOLUTION_ABILITY_ID,
  STRENGTHEN_ABILITY_ID,
  STRENGTHEN_STAT_BONUS,
  BLESSING_ABILITY_ID,
  BLESSING_LIFESTEAL_BONUS,
  LOOTER_ABILITY_ID,
  LOOTER_GOLD_PER_DEATH,
  BRUTE_ABILITY_ID,
  GLASSCANNON_ABILITY_ID,
  SPELLMAKER_ABILITY_ID,
  SPELLMASTER_ABILITY_ID,
  SPELLBURST_ABILITY_ID,
  SPELLBLAST_ABILITY_ID,
  TREASURE_HUNTER_ABILITY_ID,
  PYROTECHNIC_ABILITY_ID,
  PYROTECHNIC_BONUS_DAMAGE,
  LIFEBURST_ABILITY_ID,
  LIFEBURST_HEAL_AMOUNT,
  INSPIRED_ABILITY_ID,
  HERO_ABILITY_ID,
  SHIELD_BREAKER_ABILITY_ID,
  DIVINE_BLESSING_ABILITY_ID,
  INFESTED_ABILITY_ID,
  REPEATER_ABILITY_ID,
  LASTWISH_ABILITY_ID,
  LASTWISH_STAT_BONUS,
  SWIFT_ABILITY_ID,
  SWARMLORD_ABILITY_ID,
  SWARMNEST_ABILITY_ID,
  TREASURE_ABILITY_ID,
  TREASURE_GOLD_REWARD,
  LIFE_FULL_ABILITY_ID,
  LIFE_FULL_LIFE_BONUS,
  EXPLOSIVE_ABILITY_ID,
  GRAVEDIGGER_ABILITY_ID,
  GRANDMASTER_ABILITY_ID,
  GREATER_HEAL_ABILITY_ID,
  GREATER_HEAL_AMOUNT,
  HEAL_ABILITY_ID,
  HEIRLOOM_ABILITY_ID,
  IMPROVED_ABILITY_ID,
  IMPROVED_STAT_BONUS,
  NECROMANCY_ABILITY_ID,
  TEACHER_ABILITY_ID,
  UNDEAD_ATTACK,
  UNDEAD_HP,
  WARBAND_ABILITY_ID,
  WARBAND_ATTACK_BONUS,
} from '../shared/models/AbilityConstants';
import { MinionDefinition } from '../shared/models/MinionDefinition';
import { MinionInstance } from '../shared/models/Minion';
import {
  getSpellMinionDefinition,
  getSpellDefinitionsByLevel,
  getSpellDefinitionsUpToLevel,
  SPELL_DEFINITIONS,
} from '../shared/models/SpellDefinitions';
import { GameConfig } from '../config/gameConfig';
import { AbilityDispatcher } from './AbilityDispatcher';

// ─── Shared Ability Definitions ────────────────────────────────────────────
// Exported as a constant so both frontend and backend can reference them.
// The frontend uses these to determine which ability upgrades are eligible.
// The backend uses these to resolve the specific ability on upgrade.

const EXPLOSIVE_DAMAGE_VALUE = 3;
const HEAL_AMOUNT = 5;
const BUFF_VITALITY_AMOUNT = 3;

/**
 * Returns the Fortress HP-effect multiplier for a minion.
 *
 * Minions that own the Fortress ability take DOUBLE the effect of every
 * ability that affects their HP: heals (Heal, Lifesteal, Killer), permanent
 * max-HP buffs (Buff Vitality), ability damage (Explosive, Blast splash),
 * and the +15 HP granted on acquisition. Healing is still capped at the
 * minion's max HP unless the ability modifies its permanent max HP.
 */
export function fortressHpMultiplier(abilities: readonly string[]): number {
  return abilities.includes(FORTRESS_ABILITY_ID) ? 2 : 1;
}

/**
 * Team-union rune check for executors (COOP: every rune any player buys
 * affects BOTH players — the owner's side is the team, the union is the
 * team's purchased runes; regular matches: the owner alone). Uses the
 * GameContext's getSideMembers (absent in legacy contexts → [owner]).
 */
function sideHasRune(
  context: AbilityExecutionContext,
  owner: PlayerState,
  runeId: string
): boolean {
  const members = context.game.getSideMembers
    ? context.game.getSideMembers(owner.id)
    : [owner];
  return members.some((m) => m.hasActiveRune(runeId));
}

/**
 * Returns the HP-restoration multiplier granted by the owner's SIDE's
 * purchased runes: the Rune of Healing doubles every heal the owner's
 * minions receive from healing abilities (Lifesteal, Heal). COOP: either
 * team member's purchase applies to both (the user's team-wide rule). It
 * stacks multiplicatively with Fortress (which belongs to the HEALED
 * minion, not the rune owner). Healing caps (initial HP) are unchanged —
 * a doubled heal can never restore more than the minion's max HP.
 */
function healingRuneMultiplier(context: AbilityExecutionContext, owner: PlayerState): number {
  return sideHasRune(context, owner, RUNE_HEALING_ID) ? RUNE_HEALING_MULTIPLIER : 1;
}

export const GAME_ABILITY_DEFINITIONS: AbilityDefinition[] = [
  {
    id: 'lifesteal',
    name: 'Lifesteal',
    description: 'Heals for damage dealt when attacking',
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['warrior', 'healer'],
    availableAtLevel: 0,
  },
  {
    id: 'shield',
    name: 'Shield',
    description: 'Shield minions must be attacked before any other minion',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['tank', 'runekeeper'],
    availableAtLevel: 0,
  },
  {
    id: EXPLOSIVE_ABILITY_ID,
    name: 'Explosive',
    description: `Deals ${EXPLOSIVE_DAMAGE_VALUE} damage to ALL minions when this minion dies.`,
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['mage'],
    availableAtLevel: 0,
  },
  {
    id: 'killer',
    name: 'Killer',
    description: 'The first time this kills a minion when attacking and survives it is fully healed',
    triggerType: 'OnKill' as AbilityTriggerType,
    // WARRIOR ONLY (healer was removed — the user asked that Healers no
    // longer be offered Killer). Minions that ALREADY own Killer keep it
    // (inheritance rule — only the offer changed).
    types: ['warrior'],
    availableAtLevel: 0,
  },
  {
    id: HEAL_ABILITY_ID,
    name: 'Heal',
    description: `Restores ${HEAL_AMOUNT} HP to alive neighboring minions on attack.`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['bard', 'healer'],
    availableAtLevel: 0,
  },
  {
    id: 'buff_vitality',
    name: 'Buff Vitality',
    description: `On attack permanently adds ${BUFF_VITALITY_AMOUNT} HP to the alive minion on the left`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['bard'],
    availableAtLevel: 0,
  },
  {
    id: 'draw',
    name: 'Draw',
    description: 'Draw +1 extra card at the start of each round',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['bard'],
    availableAtLevel: 2,
  },
  {
    id: 'resurrect',
    name: 'Resurrect',
    description: 'On death, resurrects a random friendly dead (non-healer) minion with 1 HP',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['healer'],
    availableAtLevel: 5,
  },
  {
    id: FORTRESS_ABILITY_ID,
    name: 'Fortress',
    description: `Gains +${FORTRESS_HP_BONUS} HP. Doubles the effect of all abilities that give/restore health to this minion`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    // TANK ONLY (warrior was removed — the user asked that Warriors no
    // longer be offered Fortress). Minions that ALREADY own Fortress keep
    // it (inheritance rule — only the offer changed).
    types: ['tank'],
    availableAtLevel: 2,
  },
  {
    id: BLAST_ABILITY_ID,
    name: 'Blast',
    description: `On attack, the target's neighboring minions take ${Math.round(BLAST_SPLASH_FRACTION * 100)}% of its attack as damage`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['mage', 'healer'],
    availableAtLevel: 4,
  },
  {
    id: WARBAND_ABILITY_ID,
    name: 'Warband',
    description: `While alive, all friendly minions get +${WARBAND_ATTACK_BONUS} attack during combat`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['bard'],
    availableAtLevel: 1,
  },
  {
    id: NECROMANCY_ABILITY_ID,
    name: 'Necromancy',
    description: `When a neighboring minion dies in combat it is replaced by a Undead (${UNDEAD_ATTACK} attack / ${UNDEAD_HP} HP) until combat ends.`,

    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['mage', 'healer'],
    availableAtLevel: 5,
  },
  {
    id: IMPROVED_ABILITY_ID,
    name: 'Improved',
    description: `+${IMPROVED_STAT_BONUS} ATK and +${IMPROVED_STAT_BONUS} HP for every Undead YOU have played or summoned this game.`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['undead'],
    availableAtLevel: 0,
  },
  {
    id: GRAVEDIGGER_ABILITY_ID,
    name: 'Gravedigger',
    description: `Undead (${UNDEAD_ATTACK} attack / ${UNDEAD_HP} HP) is added to your draw pool effect is lost if this minion is sold.`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['mage', 'healer'],
    availableAtLevel: 3,
  },
  {
    id: DISCOUNT_ABILITY_ID,
    name: 'Discount',
    description: `While in play, buying a new card costs ${DISCOUNT_COST_REDUCTION} gold less. (but never less than 1)`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['bard'],
    availableAtLevel: 2,
  },
  {
    id: HEIRLOOM_ABILITY_ID,
    name: 'Heirloom',
    description: 'When sold, a random friendly minion on the battlefield permanently gains this card\'s current stats',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['warrior', 'bard', 'mage'],
    availableAtLevel: 2,
  },
  {
    id: TEACHER_ABILITY_ID,
    name: 'Selfless',
    description: 'Permanent stat gains this minion receives instead go to its neighbors, Keywords and heals are unaffected.',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['healer'],
    availableAtLevel: 3,
  },
  {
    id: GREATER_HEAL_ABILITY_ID,
    name: 'Greater Heal',
    description: `On attack heals ALL alive friendly minions (including this) for ${GREATER_HEAL_AMOUNT} hp - this replaces Heal.`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['healer', 'bard'],
    availableAtLevel: 3,
  },
  {
    id: BUFF_ATTACK_ABILITY_ID,
    name: 'Buff Attack',
    description: `On attack permanently adds ${BUFF_ATTACK_BONUS} ATK to the left alive neighbor`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['bard'],
    availableAtLevel: 0,
  },
  {
    id: GRANDMASTER_ABILITY_ID,
    name: 'Grandmaster',
    description: 'While in play your minions can be upgraded one extra time. This minion can never have more than 1 HP',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['healer', 'mage', 'blightcaster', 'buffer', 'runekeeper'],
    availableAtLevel: 5,
  },
  {
    id: DEATHFEEDER_ABILITY_ID,
    name: 'DeathFeeder',
    description: `Every time this minion takes damage in combat it upgrades the Improved keyword by +${DEATHFEEDER_FEED_STACKS * IMPROVED_STAT_BONUS} ATK +${DEATHFEEDER_FEED_STACKS * IMPROVED_STAT_BONUS} HP`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['tank'],
    availableAtLevel: 5,
  },
  {
    id: EXPLODING_ABILITY_ID,
    name: 'Exploding',
    description: `When alive and ANY friendly minion dies they explode and deals ${EXPLODING_DAMAGE} damage to ALL minions - replaces Explosive.`,
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['mage'],
    availableAtLevel: 3,
  },
  {
    id: SOULLINK_ABILITY_ID,
    name: 'SoulLink',
    description: 'On Death a random living friendly minion gains this minions max HP (temporary, this combat only).',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['tank'],
    availableAtLevel: 3,
  },
  {
    id: BLESSED_ABILITY_ID,
    name: 'Blessed',
    description: 'The first time this minion takes damage in combat (from any source) it gains that much HP instead of taking the damage, for this combat',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['tank'],
    availableAtLevel: 3,
  },
  {
    id: MASTER_BUFF_ABILITY_ID,
    name: 'Master Buff',
    description: `On attack, alive neighbors permanently gain +${MASTER_BUFF_STAT_BONUS} HP and +${MASTER_BUFF_STAT_BONUS} ATK - replaces Buff Vitality AND Buff Attack`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['bard', 'buffer'],
    availableAtLevel: 4,
  },
  {
    id: WARTRIBE_ABILITY_ID,
    name: 'WarTribe',
    description: `Start ofcombat, permanently gives ALL your minions +${WARTRIBE_ATK_BONUS} ATK - replaces Warband.`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['bard'],
    availableAtLevel: 4,
  },
  {
    id: HOLY_SHIELD_ABILITY_ID,
    name: 'Holy Shield',
    description: 'This minion takes half damage from Undead (rounded up)',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['tank', 'warrior'],
    availableAtLevel: 3,
  },
  {
    id: SECOND_CHANCE_ABILITY_ID,
    name: 'Second Chance',
    description: 'The FIRST time this minion would take lethal damage it is fully healed instead.',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: [
      'warrior',
      'bard',
      'mage',
      'healer',
      'tank',
      'undead',
      'blightcaster',
      'buffer',
      'runekeeper',
    ],
    availableAtLevel: 6,
  },
  // RUNEKEEPER-ONLY: `types` is exactly ['runekeeper'] — the Runekeeper
  // class is the only one that can ever be offered these
  // (isAbilityEligibleForUpgrade checks types first). RuneKeeper is the
  // Runekeeper's BASE ability (RUNEKEEPER_MINION_DEFINITION); RuneFeast is
  // learnable from level 3. The effects are live in the rune purchase path
  // (RunePurchaseRules) for every minion that carries them.
  {
    id: RUNE_KEEPER_ABILITY_ID,
    name: 'RuneKeeper',
    description: `You may buy ${RUNE_KEEPER_CAP_BONUS} additional rune per game while this minion is in play`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['runekeeper'],
    availableAtLevel: 1,
  },
  {
    id: RUNE_FEAST_ABILITY_ID,
    name: 'RuneFeast',
    description: `While in play, all runes cost ${Math.round(RUNE_FEAST_DISCOUNT_FRACTION * 100)}% less to buy, discount does NOT stack.`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['runekeeper'],
    availableAtLevel: 3,
  },
  // BLIGHTCASTER KIT (the class reaches the board via the transform system):
  // Toxic Cloud (passive — the effect lives in the RoundCoordinator's
  // per-turn cloud tick), Blunt + Mark (real OnAttack executors) and Shared
  // Pain (passive — the accumulation lives in CombatMinionCopy.takeDamage,
  // the drain in the RoundCoordinator; requires Mark — the one-way gate in
  // isAbilityEligibleForUpgrade). APPENDED — existing indices are stable.
  {
    id: TOXIC_CLOUD_ABILITY_ID,
    name: 'Toxic Cloud',
    description: `While alive, At the start of each ENEMY turn ALL enemy minions take ${TOXIC_CLOUD_DAMAGE} damage (clouds stack — two holders = ${TOXIC_CLOUD_DAMAGE * 2} damage), once per turn`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['blightcaster'],
    availableAtLevel: 3,
  },
  {
    id: BLUNT_ABILITY_ID,
    name: 'Blunt',
    description: `On attack, all enemy minions' attack is reduced by ${BLUNT_ATK_REDUCTION} (down to 0)`,

    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['blightcaster'],
    availableAtLevel: 1,
  },
  {
    id: MARK_ABILITY_ID,
    name: 'Mark',
    description: `On attacks mark the enemy minion. A marked minion takes ${MARK_EXTRA_DAMAGE} extra damage every time it takes damage`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['blightcaster'],
    availableAtLevel: 1,
  },
  {
    id: SHARED_PAIN_ABILITY_ID,
    name: 'Shared Pain',
    description: 'Every time this minion takes damage, all marked enemies also take that much damage',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['blightcaster'],
    availableAtLevel: 4,
  },
  // RUNEKEEPER KIT: Frenzy (combat aura — the effect lives in the
  // RoundCoordinator's per-turn updateFrenzyAura recompute, the Warband
  // convention) and Random Evolution (an upgrade-time ability — learning
  // it grants a RANDOM KEYWORD from any class instead of itself, resolved
  // server-side; the combat executor is a no-op). APPENDED — existing
  // indices are stable.
  {
    id: FRENZY_ABILITY_ID,
    name: 'Frenzy',
    description: `While this minion has less than half of its max HP, it has +${FRENZY_ATK_BONUS} attack (temporary, this combat only).`,

    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['runekeeper'],
    availableAtLevel: 1,
  },
  {
    id: RANDOM_EVOLUTION_ABILITY_ID,
    name: 'Random Evolution',
    description: 'When learned, this minion gains a random keyword from ANY class',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['runekeeper'],
    availableAtLevel: 1,
  },
  // BUFFER KIT (the class reaches the board via the transform system):
  // Strengthen (combat aura — the effect lives in the RoundCoordinator's
  // updateStrengthenAura recompute, the Warband convention) and Blessing
  // (combat aura — the per-attack lifesteal heal lives in
  // RoundCoordinator.applyBlessingLifesteal). Both are passive (no-op
  // executors). APPENDED — existing indices are stable.
  {
    id: STRENGTHEN_ABILITY_ID,
    name: 'Strengthen',
    description: `While alive, friendly minions have +${STRENGTHEN_STAT_BONUS} attack and +${STRENGTHEN_STAT_BONUS} HP (combat only, stacks per Strengthen holder)`,


    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['buffer'],
    availableAtLevel: 1,
  },
  {
    id: BLESSING_ABILITY_ID,
    name: 'Blessing',
    description: `While alive, friendly minions on the battlefield get +${BLESSING_LIFESTEAL_BONUS} lifesteal (combat only, stacks per living holder)`,


    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['buffer'],
    availableAtLevel: 1,
  },
  // LOOTER / BRUTE (APPENDED — existing indices are stable). Both are
  // combat effects that live in the RoundCoordinator (the death-gold
  // sites / the pre-exchange Brute application) — the executors are no-ops
  // (the passive/aura convention, like Warband / Toxic Cloud / Frenzy).
  {
    id: LOOTER_ABILITY_ID,
    name: 'Looter',
    description: `Gains ${LOOTER_GOLD_PER_DEATH} additional gold for every enemy minion that dies in combat (stacks per living Looter on your side)`,

    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['warrior', 'runekeeper'],
    availableAtLevel: 5,
  },
  {
    id: BRUTE_ABILITY_ID,
    name: 'Brute',
    description: 'On attack, attack is doubled and HP is halved (never below 1) — temporary, for the rest of this combat',

    triggerType: 'OnCombatStart' as AbilityTriggerType,
    // Tank was REMOVED from the offer list (a Tank halving its own HP
    // trades its identity for a burst); a minion that ALREADY carries the
    // keyword keeps working (the combat effect is class-agnostic).
    types: ['warrior', 'mage'],
    availableAtLevel: 2,
  },
  // FREEZE (Mage/Bard, level 4 — task 221 raised it from 3) — APPENDED,
  // existing indices are stable. 75% chance to freeze the TARGET, 25% to
  // freeze ONE of the target's neighbors; a frozen minion unfreezes and
  // skips its next attack. The effect lives in the RoundCoordinator
  // (applyFreeze + the skip in the attack loop) — the executor is a no-op
  // (the passive convention, like Warband / Brute).
  {
    id: FREEZE_ABILITY_ID,
    name: 'Freeze',
    description: `Every attack has a ${Math.round(FREEZE_TARGET_CHANCE * 100)}% chance to freeze the target - a ${Math.round(FREEZE_NEIGHBOR_CHANCE * 100)}% chance to freeze one of the target's neighbors instead.`,
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['mage', 'bard'],
    availableAtLevel: 4,
  },
  // GLASSCANNON (Swarmer, level 0 — the class's BASE ability) — APPENDED,
  // existing indices are stable. A REAL OnAttack executor: the holder dies
  // after it has attacked (the RoundCoordinator's death pipeline finalizes
  // the corpse; Swarmer tokens pay no kill gold and count for no kill
  // credit — the token rules in AbilityConstants).
  {
    id: GLASSCANNON_ABILITY_ID,
    name: 'Fragile',
    description: 'This minion dies after it has attacked.',
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['swarmer'],
    availableAtLevel: 0,
  },
  // SWARMLORD (Mage/Blightcaster, level 5 — lowered from 6) — APPENDED,
  // existing indices are stable. When the holder dies, the owner's board
  // is filled with Swarmers that all attack immediately (before the
  // enemy's next turn). The effect lives in the RoundCoordinator
  // (_resolveSwarmLordBursts — end of every attack exchange); the
  // executor is a no-op (the passive convention).
  {
    id: SWARMLORD_ABILITY_ID,
    name: 'SwarmLord',
    description: 'When this minion dies, it fills your board with Swarmers that all attack immediately.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['mage', 'blightcaster'],
    availableAtLevel: 5,
  },
  // TREASURE / LIFE-FULL (Items, level 0 — the item class's BASE abilities)
  // — APPENDED, existing indices are stable (43/44). `types: ['item']`
  // makes them unreachable by every other class (the types check runs
  // first). Items can never be upgraded, so the level requirement of 0
  // ("always available") only matters for the shared eligibility matrix.
  // Both effects live in the RoundCoordinator's centralized death-gold
  // engine (_awardDeathGold) — the executors are no-ops (the passive
  // convention, like Looter).
  {
    id: TREASURE_ABILITY_ID,
    name: 'Treasure',
    description: `The player who kills this item gains ${TREASURE_GOLD_REWARD} gold.`,
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['item'],
    availableAtLevel: 0,
  },
  {
    id: LIFE_FULL_ABILITY_ID,
    name: 'Life-full',
    description: `The player who kills this item instantly gains ${LIFE_FULL_LIFE_BONUS} life.`,
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['item'],
    availableAtLevel: 0,
  },
  // SWARMNEST (Mage/Blightcaster, level 6) — APPENDED, existing indices
  // are stable (45). ONE-WAY GATE: offered ONLY to a minion that already
  // has SwarmLord (isAbilityEligibleForUpgrade — the Shared Pain / Mark
  // pattern; SwarmLord is NOT removed). EFFECT: the holder's SwarmLord
  // burst spawns Swarmers WITHOUT the Fragile keyword (they survive their
  // one attack). The effect lives in the RoundCoordinator's swarmer spawn
  // (_runSwarmLordBurst); the executor is a no-op (the passive
  // convention).
  {
    id: SWARMNEST_ABILITY_ID,
    name: 'SwarmNest',
    description: 'Your Swarmers spawn without the Fragile keyword.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['mage', 'blightcaster'],
    availableAtLevel: 6,
  },
  // SPELLMAKER (Spellmaker, level 1) — APPENDED, existing indices are
  // stable (46). The Spellmaker class's BASE keyword: while the holder is
  // on its owner's battlefield, spells join the owner's draw pile (one
  // spell source — chance equal to one class card, level-1 spells), and
  // each additional source (Spellmaker / SpellMaster / the Rune of the
  // Mage) IMPROVES the pool (up to level N spells) at N× the chance (the
  // sources stack). The effect lives in the draw services
  // (CardDrawService.drawCardForPlayerSpellPower — the round draw in
  // RoundCoordinator + the purchase draw in GameSessionService); the
  // executor is a no-op (the passive convention).
  {
    id: SPELLMAKER_ABILITY_ID,
    name: 'Spellmaker',
    description:
      'When on your battlefield, adds spells to your draw pile. If spells are already in your pile, it improves the spells in your pile.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['spellmaker'],
    availableAtLevel: 1,
  },
  // SPELLMASTER (Spellmaker, level 5) — APPENDED (47). ONE-WAY REPLACEMENT
  // of Spellmaker (abilitiesReplacedByUpgrade removes Spellmaker on the
  // upgrade; Spellmaker is never re-offered to a SpellMaster holder — the
  // Greater Heal / Heal pattern). Same effect as Spellmaker, but it COUNTS
  // TWICE: power 2 directly (level-2 spells at 2× the chance), stacking
  // with other sources. The executor is a no-op (the passive convention).
  {
    id: SPELLMASTER_ABILITY_ID,
    name: 'SpellMaster',
    description:
      'As Spellmaker, but counts twice: level-2 spells at 2× the chance, stacking with other Spellmakers, SpellMasters and the Rune of the Mage.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['spellmaker'],
    availableAtLevel: 5,
  },
  // ─── Spell-granted keywords (Task: New Spells) — APPENDED, existing
  // indices are stable (48/49/50). `types: []` makes them CATALOG-ONLY:
  // no class can acquire them through an upgrade (the types check runs
  // first) — the spell cards (SpellDefinitions) are the sole source. All
  // three are passives with NO-OP executors (the passive convention); the
  // effects live in the combat engine (see AbilityConstants).
  // TREASURE HUNTER (spell, level 2, one-combat rework task 196): the
  // keyword lasts ONE combat only (the RoundCoordinator records the holder
  // at session creation and the post-combat step removes the keyword —
  // the minion stays). During that combat, the first time the holder
  // kills an enemy minion, the corpse is replaced IN COMBAT by a golden
  // treasure (the Treasure Chest item as a temporary `treasure_` combat
  // token — it belongs to the enemy, who can kill it for its 25-gold
  // Treasure reward; discarded at combat end, no board insertion).
  {
    id: TREASURE_HUNTER_ABILITY_ID,
    name: 'Treasure Hunter',
    description:
      'For the next combat only: the first enemy minion this minion kills is replaced by a golden treasure',
    triggerType: 'OnKill' as AbilityTriggerType,
    types: [],
    availableAtLevel: 2,
  },
  // DIVINE BLESSING (spell, level 1): the FIRST damage the holder takes in
  // combat is ignored (the whole damage instance — the effect lives in
  // CombatMinionCopy.takeDamage, the single damage choke point; streams as
  // an 'ability' event — the client's large yellow explosion).
  {
    id: DIVINE_BLESSING_ABILITY_ID,
    name: 'Divine Blessing',
    description:
      'The first time this minion takes damage in combat, the damage is ignored.',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: [],
    availableAtLevel: 1,
  },
  // INFESTED (spell, level 2): the spell grants the SWARMLORD keyword
  // IMMEDIATELY (temporarily — the minion.infestedSwarmLord marker); the
  // keyword lasts ONE combat only — the RoundCoordinator records the holder
  // at session creation (_recordInfestedHolders) and the post-combat step
  // removes the keyword (the minion stays; a class-granted SwarmLord is
  // never touched).
  {
    id: INFESTED_ABILITY_ID,
    name: 'Infested',
    description:
      'This minion gains the SwarmLord keyword. It keeps it for one combat only.',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: [],
    availableAtLevel: 2,
  },
  // SPELLBURST (Spellmaker / Runekeeper, level 5) + SPELLBLAST (level 6)
  // — APPENDED, existing indices are stable (51/52). Death-time spell
  // grants (see AbilityConstants for the full rules; SpellBlast replaces
  // Spellburst — the Greater Heal / Heal pattern).
  {
    id: SPELLBURST_ABILITY_ID,
    name: 'Spellburst',
    description: 'When this minion dies, you receive a random level 1 spell.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['spellmaker', 'runekeeper'],
    availableAtLevel: 5,
  },
  {
    id: SPELLBLAST_ABILITY_ID,
    name: 'SpellBlast',
    description: 'When this minion dies, you receive a random spell of any level.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['spellmaker', 'runekeeper'],
    availableAtLevel: 6,
  },
  // REPEATER (Spellmaker, level 3 — task 184) — APPENDED, existing indices
  // are stable (53). START-OF-COMBAT: the holder re-casts the last
  // minion-targeting spell that was cast ON THE HOLDER (the persistent
  // MinionInstance.lastCastSpellId) on BOTH of its battlefield neighbors —
  // the spell's full effect (permanent stats/keywords/counters: an
  // Upgraded last cast advances the caster's escalation ladder, a Protector
  // shields both neighbors, …). Keyword grants are mirrored onto the
  // neighbors' COMBAT copies so the one-combat / in-combat keywords work in
  // THIS combat; Infested re-casts are recorded for the post-combat removal.
  // The effect lives in the RoundCoordinator's applyRepeaterCasts (top of
  // runCombatToCompletion — it needs the session); the executor is a no-op
  // (the passive convention).
  {
    id: REPEATER_ABILITY_ID,
    name: 'Repeater',
    description:
      'Start of combat: cast the last spell that was cast on this minion on its neighbors.',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['spellmaker'],
    availableAtLevel: 3,
  },
  // LASTWISH (Buffer, level 3 — task 192) — APPENDED, existing indices
  // (and every index-pinned test) stay stable.
  {
    id: LASTWISH_ABILITY_ID,
    name: 'Last Wish',
    description:
      'When this minion dies, all living minions on its side get +5 HP and +5 attack for the rest of this combat.',
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['buffer'],
    availableAtLevel: 3,
  },
  // SWIFT (Warrior/Blightcaster/Mage, level 3 — task 192) — APPENDED.
  // The double attack is a SEQUENCE behavior (RoundCoordinator's combat
  // loop + CombatSequence.queueImmediateReattack) — the executor is a
  // no-op (same convention as Repeater / SwarmLord). OnAttack keeps the
  // standard ability-trigger event stream (the client's FX for the second
  // lunge is the second 'attack' event itself).
  {
    id: SWIFT_ABILITY_ID,
    name: 'Swift',
    description:
      'This minion attacks twice',
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['warrior', 'blightcaster', 'mage'],
    availableAtLevel: 3,
  },
  // PYROTECHNIC (Mage, level 4 — task 198) — APPENDED, existing indices
  // (and every index-pinned test) stay stable.
  {
    id: PYROTECHNIC_ABILITY_ID,
    name: 'Pyrotechnic',
    description: `While this minion is alive, the Explosive and Exploding blasts of all your minions deal +${PYROTECHNIC_BONUS_DAMAGE} more damage (effect STACKS: additional living Pyrotechnic adds another +${PYROTECHNIC_BONUS_DAMAGE}).`,
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: ['mage'],
    availableAtLevel: 4,
  },
  // LIFEBURST (Healer, level 2 — task 206) — APPENDED, existing indices
  // (and every index-pinned test) stay stable (57). OnDeath: every
  // minion on the board (BOTH sides) is healed for 12 HP at the moment
  // the holder dies (the executor below).
  {
    id: LIFEBURST_ABILITY_ID,
    name: 'LifeBurst',
    description: `When this minion dies, ALL minions are healed for ${LIFEBURST_HEAL_AMOUNT} HP.`,
    triggerType: 'OnDeath' as AbilityTriggerType,
    types: ['healer'],
    availableAtLevel: 2,
  },
  // INSPIRED (any class, level 0 — task 207) — APPENDED, existing
  // indices (and every index-pinned test) stay stable (58). TEMPORARY
  // keyword: CATALOG-ONLY (`types: []` — no class can learn it through
  // an upgrade; the Hero keyword — and future effects — grant it for
  // one combat only). +2/+2 for the rest of the combat; re-grants
  // DOUBLE the keyword's current boosting stats (4 → 8 → 16). The effect
  // lives in the RoundCoordinator's Hero grant path (the executor is a
  // no-op — the passive convention).
  {
    id: INSPIRED_ABILITY_ID,
    name: 'Inspired',
    description: 'Gives +4 HP / +4 attack for this combat (Grandmaster minions gain no HP — they always have 1 HP). When a minion receives it again, the stats it gives are doubled',
    triggerType: 'OnCombatStart' as AbilityTriggerType,
    types: [],
    availableAtLevel: 0,
  },
  // HERO (Warrior, level 6 — task 207) — APPENDED (59). OnKill (attack
  // kills only — a defensive counter-kill never triggers it): every time
  // the Hero kills a minion, ALL OTHER living friendly minions receive
  // the temporary Inspired keyword (per KILL — several kills in one
  // exchange grant several times; re-grants double the current Inspired
  // boosting stats). The effect lives in the RoundCoordinator's OnKill
  // phase (the executor is a no-op — the passive convention).
  {
    id: HERO_ABILITY_ID,
    name: 'Hero',
    description: 'When this minion kills a minion during an attack, all other friendly minions gain Inspired for the rest of this combat.',
    triggerType: 'OnKill' as AbilityTriggerType,
    types: ['warrior'],
    availableAtLevel: 6,
  },
  // SHIELD BREAKER (Warrior/Blightcaster, level requirement 3) — APPENDED
  // (60), existing indices are stable. When this unit ATTACKS a unit with
  // the SHIELD keyword (taunt), that unit's Shield is temporarily removed
  // for the rest of THIS combat (the combat copy loses the 'shield'
  // ability — targeting rules stop forcing attacks at it) and restored
  // afterward (combat-copy-only — the persistent minion keeps its
  // Shield). The effect lives in the RoundCoordinator's attack loop
  // (_applyShieldBreaker — right after the attack event) which streams the
  // dedicated 'shieldBroken' event (the client's red shield-shatter burst
  // + the Shield badge drop). The executor is a no-op (the passive
  // convention, like Brute / Toxic Cloud).
  {
    id: SHIELD_BREAKER_ABILITY_ID,
    name: 'Shield Breaker',
    description: 'When this minion attacks a unit with Shield, that unit loses its Shield for the rest of this combat (restored afterward).',
    triggerType: 'OnAttack' as AbilityTriggerType,
    types: ['warrior', 'blightcaster'],
    availableAtLevel: 3,
  },
];

// ─── Ability 1: Lifesteal ──────────────────────────────────────────────────
// Heals the attacking minion for the damage it deals.
// Only triggers on attack (not when defending).
// Cannot heal past the minion's initial HP at start of combat.
// NEVER resurrects: the executor runs in the OnAttack window, AFTER the
// exchange's simultaneous damage (hit + counter-attack). A minion the
// exchange dropped to 0 HP stays dead — the heal is netted against the
// damage, it does not bring the minion back (1 HP lifesteal vs a 10-ATK
// counter = 1-10+1 = -8 → dead, not 0→1).

const LIFESTEAL_ID = 'lifesteal';

const lifestealExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const attackerCopy = eventData.attackerCopy;

  if (!attackerCopy) return; // No combat copy available (non-combat trigger)

  // Lethal counter-attack: the exchange already killed the attacker. The
  // heal never resurrects — a dead minion stays dead (see the block comment
  // above). Alive wounded attackers still heal as usual.
  if (!attackerCopy.isAlive) return;

  // Damage dealt = attacker's attack value. Fortress minions double the
  // heal, and the owner's Rune of Healing doubles it again (multiplicative).
  const damageDealt = attackerCopy.attack
    * fortressHpMultiplier(attackerCopy.abilities)
    * healingRuneMultiplier(context, context.owner);

  // Heal the attacker, but cap at initial HP (healing can never exceed max HP)
  const currentHp = attackerCopy.currentHp;
  const initialHp = attackerCopy.initialHp;
  const maxHeal = initialHp - currentHp; // How much can be healed

  if (maxHeal <= 0) return; // Already at or above max

  const actualHeal = Math.min(damageDealt, maxHeal);
  attackerCopy.heal(actualHeal);
};

// ─── Ability 2: Shield (Taunt) ─────────────────────────────────────────────
// Shield minions must be attacked and killed before any other minion.
// This is enforced by the combat target selection logic (selectTargetWithShieldPriority),
// not by this executor. The executor is a no-op — the ability's presence in the
// abilities array is what matters for target selection.

const SHIELD_ID = 'shield';

const shieldExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // Shield is enforced at target selection time, not via ability execution.
  // This executor is a no-op placeholder.
};

// ─── Ability 3: Explosive ──────────────────────────────────────────────────
// When this minion dies, deals 3 damage to ALL minions on the battlefield
// (both the owner's minions and the opponent's minions).

const EXPLOSIVE_ID = EXPLOSIVE_ABILITY_ID;
const EXPLOSIVE_DAMAGE = 3;

const explosiveExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnDeathEventData;
  const allCopies = eventData.allCombatCopies;

  if (!allCopies) return; // No combat copies available (non-combat death)

  // Snapshot all alive minions BEFORE dealing damage.
  // This ensures damage is dealt simultaneously (in parallel) to all minions
  // that were alive at the moment of the explosion, regardless of whether
  // earlier minions in the iteration died from the blast.
  // The OWNER of each target is tracked alongside it (the deceased's owner
  // for ownCopies, context.opposingOwner — supplied by the RoundCoordinator
  // — for the opposing copies) so the Boom Resistant check below can be
  // applied per owner.
  const aliveTargets: Array<{ copy: CombatMinionCopy; owner: PlayerState | undefined }> = [];
  for (const [, copy] of allCopies.ownCopies) {
    if (copy.isAlive) aliveTargets.push({ copy, owner: context.owner });
  }
  for (const [, copy] of allCopies.opposingCopies) {
    if (copy.isAlive) aliveTargets.push({ copy, owner: context.opposingOwner });
  }

  // PYROTECHNIC (task 198): the blast's damage is EXPLOSIVE_DAMAGE plus
  // PYROTECHNIC_BONUS_DAMAGE per ALIVE Pyrotechnic minion on the BLAST
  // OWNER's side (stacks — two living holders = +6). The count is read
  // live from the snapshot: the deceased (isAlive = false at the OnDeath
  // window) never counts for its own blast. The bonus is part of the
  // ability damage — a Fortress victim doubles it with the rest.
  const pyrotechnicCount = [...allCopies.ownCopies.values()].filter(
    (c) => c.isAlive && c.abilities.includes(PYROTECHNIC_ABILITY_ID),
  ).length;
  const blastDamage = EXPLOSIVE_DAMAGE + PYROTECHNIC_BONUS_DAMAGE * pyrotechnicCount;

  // Deal damage to all snapshot targets simultaneously.
  // Fortress minions take double the ability damage.
  for (const { copy, owner } of aliveTargets) {
    // Boom Resistant (the TARGET's owner's SIDE — COOP: either team member's
    // purchase protects the whole team): immune copies take NO damage from
    // the blast — from any source, including their own side's explosion
    // (friendly fire). An unknown owner (a caller that does not supply
    // context.opposingOwner) stays vulnerable: no owner-specific rule can be
    // checked.
    if (owner && sideHasRune(context, owner, RUNE_BOOM_RESISTANT_ID)) continue;
    // Fortress victims take double the ability damage. The source class is
    // the DECEASED's (the blast owner) — a Holy Shield victim halves the
    // blast when the exploding minion is Undead-class.
    copy.takeDamage(
      blastDamage * fortressHpMultiplier(copy.abilities),
      context.sourceMinion.minionType
    );
  }
};

// ─── Ability 4: Killer ─────────────────────────────────────────────────────
// When this minion scores its FIRST ATTACKING kill in combat, it fully
// heals to its initial HP (the HP it had at the start of combat). Later
// kills in the same combat do NOT heal. Kill tracking lives on the combat
// copy (hasKilledInCombat), which is shared across all OnKill dispatches for
// the unit during the combat and discarded when combat ends — so the flag
// resets automatically for every new combat.
//
// Attacking-kill gate: the RoundCoordinator only dispatches OnKill when the
// killer was the ATTACKER of the exchange (a kill scored by counter-attack
// never reaches this executor), so a defending Killer cannot heal itself on
// an enemy's death. The first kill that DOES trigger the heal consumes the
// flag — defensive kills before it do not.
//
// "Kills a minion and survives" — the survivor clause is not a resurrection
// clause: OnKill dispatches AFTER the exchange's simultaneous damage, and a
// killer the exchange dropped to 0 HP stays dead (the full heal is skipped,
// the kill credit/gold/death bookkeeping all treat it as a corpse).

const KILLER_ID = 'killer';

const killerExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnKillEventData;
  const killerCopy = eventData.killerCopy;

  if (!killerCopy) return; // No combat copy available (non-combat trigger)

  // Only the unit's FIRST kill of this combat triggers the heal. The flag is
  // consumed even when there is nothing to heal (already at full HP — or the
  // killer is a corpse, below), so a later damaged kill cannot "save" the heal.
  if (killerCopy.hasKilledInCombat) return;
  killerCopy.hasKilledInCombat = true;

  // Lethal counter-attack: the exchange already killed the killer. The full
  // heal never resurrects — it scored the kill and died to the exchange, so
  // it stays dead (see the block comment above).
  if (!killerCopy.isAlive) return;

  // Fully heal to initial HP. Fortress doubles the healing effect, but
  // healing can never exceed max HP (the cap binds, so the result is the
  // same full heal).
  const currentHp = killerCopy.currentHp;
  const initialHp = killerCopy.initialHp;
  const healAmount = initialHp - currentHp;

  if (healAmount > 0) {
    const doubled = healAmount * fortressHpMultiplier(killerCopy.abilities);
    killerCopy.heal(Math.min(doubled, initialHp - currentHp));
  }
};

// ─── Ability 5: Heal ───────────────────────────────────────────────────────
// When this minion attacks, restores 5 HP to alive neighboring minions
// on the owner's side (left and right neighbors). Only targets alive minions.
// Heals combat copies (temporary — discarded after combat).
// Cannot heal past the neighbor's initial HP at start of combat.

const HEAL_ID = HEAL_ABILITY_ID;

const healExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const combatCopies = context.combatCopies;

  if (!combatCopies) return; // No combat copies available (non-combat trigger)

  // Find the attacker's battlefield position
  const attackerPosition = eventData.attackerCopy?.battlefieldPosition
    ?? context.sourceMinion.battlefieldPosition;

  if (attackerPosition < 0) return; // Not on battlefield

  // Find alive neighbors (position - 1 and position + 1)
  const neighborPositions = [attackerPosition - 1, attackerPosition + 1];

  for (const neighborPos of neighborPositions) {
    // Find the combat copy at this position
    let neighborCopy: CombatMinionCopy | null = null;
    let neighborInstanceId: string | null = null;
    for (const [instanceId, copy] of combatCopies) {
      if (copy.battlefieldPosition === neighborPos) {
        neighborCopy = copy;
        neighborInstanceId = instanceId;
        break;
      }
    }

    if (!neighborCopy || !neighborCopy.isAlive) continue; // Skip dead or missing neighbors

    // Fortress neighbors double the heal, and the healer's owner's Rune of
    // Healing doubles it again (multiplicative).
    const currentHp = neighborCopy.currentHp;
    const initialHp = neighborCopy.initialHp;
    const rawHeal =
      HEAL_AMOUNT * fortressHpMultiplier(neighborCopy.abilities) * healingRuneMultiplier(context, context.owner);

    // Over-feed (the target's owner's purchased rune): the heal is NOT
    // capped at the max HP — the overflow past the cap RAISES the temporary
    // max HP (the copy's heal cap) by the overflow for the rest of THIS
    // combat (a 5-max minion healed for 5 at full HP ends at 10/10). The
    // raised cap is what heal-capped abilities (Lifesteal, Killer) can then
    // heal up to in the same combat. Grandmaster targets keep their 1-HP
    // cap — their HP gains are ignored (same convention as the HP-buff
    // guards elsewhere).
    const overFeed =
      sideHasRune(context, context.owner, RUNE_OVERFEED_ID) &&
      !neighborCopy.abilities.includes(GRANDMASTER_ABILITY_ID);

    let actualHeal: number;
    if (overFeed) {
      actualHeal = rawHeal; // never capped
      neighborCopy.heal(rawHeal);
      const overflow = Math.max(0, neighborCopy.currentHp - initialHp);
      if (overflow > 0) {
        // initialHp is the max-HP/heal cap — raise it like the other
        // in-combat HP buffs (the established cast pattern).
        (neighborCopy as { initialHp: number }).initialHp += overflow;
      }
    } else {
      const maxHeal = initialHp - currentHp;
      if (maxHeal <= 0) continue; // Already at or above max
      actualHeal = Math.min(rawHeal, maxHeal); // capped at initial HP (Lifesteal convention)
      neighborCopy.heal(actualHeal);
    }

    // Achievement observation (Healing Voice): report the HP actually
    // restored (the uncapped amount under Over-feed) — the hook is a no-op
    // when absent.
    if (neighborInstanceId) {
      context.abilityEffectHooks?.onHealApplied?.(neighborInstanceId, actualHeal);
    }
  }
};

// ─── Ability 6: Buff Vitality ──────────────────────────────────────────────
// When this minion attacks, permanently adds 3 HP to the alive minion
// immediately to the LEFT on the owner's side. This is a permanent stat
// increase — it persists after combat ends, even though the battlefield
// is restored from the pre-combat snapshot.
// Only targets alive neighbors.
// EVERY ATTACK: the buff applies on EVERY attack the owner makes (the
// user's rule — a two-attack combat adds +3 per attack, +6 total). The
// gain is MERGED into the recorded permanent change (spread-existing), so
// the portions of a unit holding BOTH buffs are never dropped.

const BUFF_VITALITY_ID = 'buff_vitality';

const buffVitalityExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const combatCopies = context.combatCopies;

  if (!combatCopies) return; // No combat copies available (non-combat trigger)

  // Find the attacker's battlefield position
  const attackerPosition = eventData.attackerCopy?.battlefieldPosition
    ?? context.sourceMinion.battlefieldPosition;

  if (attackerPosition < 0) return; // Not on battlefield

  // Find the left neighbor (position - 1)
  const leftNeighborPos = attackerPosition - 1;

  let leftNeighborCopy: CombatMinionCopy | null = null;
  let leftNeighborInstanceId: string | null = null;
  for (const [instanceId, copy] of combatCopies) {
    if (copy.battlefieldPosition === leftNeighborPos) {
      leftNeighborCopy = copy;
      leftNeighborInstanceId = instanceId;
      break;
    }
  }

  if (!leftNeighborCopy || !leftNeighborCopy.isAlive) return; // Skip dead or missing neighbor
  if (!leftNeighborInstanceId) return;

  // Grandmaster: the target can never have more than 1 HP, so an HP buff
  // aimed directly at it is ignored entirely — neither the in-combat
  // portion nor the permanent change is recorded (the target's abilities
  // are static for the combat, so it would never become a valid target
  // anyway).
  if (leftNeighborCopy.abilities.includes(GRANDMASTER_ABILITY_ID)) return;

  // Fortress neighbors double the permanent max-HP buff (3 becomes 6).
  const vitalityAmount = BUFF_VITALITY_AMOUNT * fortressHpMultiplier(leftNeighborCopy.abilities);

  // Record permanent HP increase keyed by the buff's TARGET. Teacher
  // redirection is applied later at the single choke point
  // (RoundCoordinator.applyPermanentStatChanges →
  // player.redirectTeacherStatGain), using the same deterministic cascade
  // as the in-combat portion below — combat positions are fixed, so both
  // resolutions always agree on the final recipients.
  if (context.permanentStatChanges) {
    // MERGE (spread existing): a unit holding BOTH buffs records two
    // entries for the same target — replacing the entry would silently
    // drop the other ability's portion (the buff recorded second wiped
    // the first's permanent change).
    const existing = context.permanentStatChanges.get(leftNeighborInstanceId) ?? {};
    context.permanentStatChanges.set(leftNeighborInstanceId, {
      ...existing,
      hpChange: (existing.hpChange ?? 0) + vitalityAmount,
    });
  }

  // In-combat portion: raise the currentHp AND initialHp (readonly — type
  // assertion) of the combat copies that actually hold the buff during THIS
  // combat, so heal-capped abilities (e.g., Lifesteal) respect the new max
  // HP. If the target has Teacher, the Teacher receives NOTHING even during
  // the combat: the same deterministic cascade
  // (PlayerState.resolveTeacherStatRedistribution) decides which neighbor
  // copies get the boost, matching the post-combat persistent result.
  const originMinion = context.owner.minionInstances.get(leftNeighborInstanceId);
  const recipients = originMinion
    ? context.owner.resolveTeacherStatRedistribution(originMinion, vitalityAmount, 0)
    : [];

  if (recipients.length > 0) {
    for (const recipient of recipients) {
      const copy = context.combatCopies?.get(recipient.instanceId);
      // DEAD recipients get NOTHING (the user's rule: Selfless/Teacher must
      // not give stats to dead neighbors) — the in-combat portion skips
      // them. The post-combat permanent application restores the minion
      // (alive again), so the persistent gain still lands then.
      if (!copy || !copy.isAlive) continue;
      // Grandmaster recipient (end of a Teacher cascade): its HP can never
      // exceed 1 — the boost is ignored, exactly like the persistent side
      // (gainHp clamps there too). The hook reports HP actually applied,
      // so a fully ignored boost reports nothing.
      const isGrandmasterTarget = copy.abilities.includes(GRANDMASTER_ABILITY_ID);
      if (isGrandmasterTarget) continue;
      // Achievement observation (Healthy Undead): the permanent max-HP
      // boost applies to every ACTUAL recipient (post-Teacher-redirect),
      // independent of the in-combat copy bookkeeping below.
      context.abilityEffectHooks?.onVitalityBoostApplied?.(recipient.instanceId, recipient.hpGain);
      copy.currentHp += recipient.hpGain;
      (copy as { initialHp: number }).initialHp += recipient.hpGain;
    }
  } else {
    // No redirect (target lacks Teacher, or a Teacher with no neighbors
    // keeps it for itself — same target either way).
    leftNeighborCopy.currentHp += vitalityAmount;
    (leftNeighborCopy as { initialHp: number }).initialHp += vitalityAmount;
    context.abilityEffectHooks?.onVitalityBoostApplied?.(leftNeighborInstanceId, vitalityAmount);
  }
};

// ─── Ability 7: Draw ──────────────────────────────────────────────────────
// Passive aura: +1 card drawn at the start of each round while this minion
// is on the battlefield. The actual draw bonus is applied in RoundCoordinator
// by checking for this ability ID on the battlefield. The executor is a
// no-op — the presence of the ability ID is what matters.

const DRAW_ID = 'draw';

const drawExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // Draw bonus is applied passively in RoundCoordinator.runRound()
  // by checking for 'draw' ability on the battlefield.
  // This executor is a no-op placeholder.
};

// ─── Ability 8: Resurrect ─────────────────────────────────────────────────
// When this minion dies during combat, resurrects a random friendly minion
// that is currently dead (HP <= 0) and is NOT a healer, setting its HP to 1.
// Uses the match's RNG for truly random (but deterministic with same seed)
// selection. Only operates on combat copies.
//
// COMBAT SPECIAL CASE — DEFERRED RESOLUTION ("if there is a Resurrect,
// resolve it last"): in combat, the RoundCoordinator does NOT let this
// executor run inside the OnDeath window. The window skips it
// (dispatchOnDeath skipAbilities) and queues the request on the
// CombatSession (deferredResurrects); ALL queued Resurrects fire after
// every death of the exchange has been fully resolved (OnDeath windows,
// the explosion chain, SoulLink, the rune death effects, Necromancy, the
// grief passes) — _resolveDeferredResurrects at the end of the exchange /
// after the start-of-combat passes, just before the next minion's turn —
// with this executor's exact eligibility rules (random dead friendly
// non-healer card, skipping the holder). This keeps the explosion chain
// free of mid-chain resurrections: a minion that dies WITH Exploding
// always explodes first (it DID die) and is resurrected only afterwards,
// as a fresh living minion for the next exchange. Direct dispatcher
// dispatches (tests) still run this executor immediately — the deferral
// is the coordinator's job, not the executor's.

const RESURRECT_ID = 'resurrect';

const resurrectExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const combatCopies = context.combatCopies;
  if (!combatCopies) return; // No combat copies available (non-combat death)

  // Collect all dead friendly minions that are NOT healers.
  const deadNonHealers: CombatMinionCopy[] = [];
  for (const [instanceId, copy] of combatCopies) {
    // Skip the deceased minion itself (the one with Resurrect)
    if (instanceId === context.sourceMinion.instanceId) continue;

    // Must be dead (HP <= 0)
    if (copy.isAlive) continue;

    // Must NOT be a healer type
    const minion = context.owner.minionInstances.get(instanceId);
    if (!minion) continue;
    if (minion.minionType === 'healer') continue;

    deadNonHealers.push(copy);
  }

  if (deadNonHealers.length === 0) return; // No eligible minions to resurrect

  // Pick a random eligible minion using the match's RNG
  const chosenIndex = context.game.rng.nextInt(0, deadNonHealers.length);
  const chosenCopy = deadNonHealers[chosenIndex];
  chosenCopy.currentHp = 1;
  chosenCopy.isAlive = true; // Update alive status since HP is now > 0
};

// ─── Ability 9: Fortress ───────────────────────────────────────────────────
// Passive (Tank, Warrior, level requirement 2).
// The +15 HP is granted PERMANENTLY when the ability is acquired — see
// MinionInstance.applyUpgrade, the single choke point through which minions
// gain abilities (player upgrades and bot turns both route through it).
// The HP-doubling effect is applied INSIDE the other abilities' executors
// (Lifesteal, Killer, Heal, Buff Vitality, Explosive, Blast) via
// fortressHpMultiplier(). Healing remains capped at max HP unless the
// ability modifies the permanent max HP (e.g., Buff Vitality).
// This executor is a no-op placeholder — the ability's presence is what matters.

const FORTRESS_ID = FORTRESS_ABILITY_ID;

const fortressExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Fortress section above.
};

// ─── Ability 10: Blast ─────────────────────────────────────────────────────
// OnAttack (Mage, Healer, level requirement 4).
// When this minion attacks, the TARGET's neighboring minions (on the
// defender's side, at the target's battlefield position ± 1) take 50% of
// the attacker's attack as splash damage (floored). Only alive neighbors
// are hit. A 10-ATK Blast: target takes 10 (normal combat), each alive
// neighbor takes 5. The splash lands even if the target itself died from
// the main hit. Combat copies only — discarded after combat.

const BLAST_ID = BLAST_ABILITY_ID;

const blastExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const attackerCopy = eventData.attackerCopy;
  const defenderCopy = eventData.defenderCopy;
  const opposingCopies = context.opposingCombatCopies;

  if (!attackerCopy || !defenderCopy || !opposingCopies) {
    return; // No combat copies available (non-combat trigger)
  }

  const splashDamage = Math.floor(attackerCopy.attack * BLAST_SPLASH_FRACTION);
  if (splashDamage <= 0) return; // 0- or 1-ATK attackers produce no splash

  const targetPosition = defenderCopy.battlefieldPosition;
  if (targetPosition < 0) return; // Target not on battlefield

  for (const neighborPos of [targetPosition - 1, targetPosition + 1]) {
    let neighborCopy: CombatMinionCopy | null = null;
    for (const copy of opposingCopies.values()) {
      if (copy.battlefieldPosition === neighborPos) {
        neighborCopy = copy;
        break;
      }
    }

    if (!neighborCopy || !neighborCopy.isAlive) continue; // Skip dead or missing neighbors

    // Fortress neighbors take double the ability damage. The source class is
    // the ATTACKER (the Blast owner) — a Holy Shield neighbor halves the
    // splash when the attacker is Undead-class.
    neighborCopy.takeDamage(
      splashDamage * fortressHpMultiplier(neighborCopy.abilities),
      attackerCopy.minionType
    );
  }
};

// ─── Ability 11: Warband ───────────────────────────────────────────────────
// Passive aura (Bard, level requirement 1).
// While a Warband minion is alive, ALL friendly combat copies get +2 attack
// for the duration of combat. The aura is applied by RoundCoordinator
// (updateWarbandAura): at combat start and re-evaluated after every death,
// so the bonus is removed when the last Warband minion dies and restored if
// one is resurrected. Combat-only — combat copies are discarded after
// combat, so nothing persists to the persistent battlefield.
// This executor is a no-op placeholder — the ability's presence is what matters.

const WARBAND_ID = WARBAND_ABILITY_ID;

const warbandExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: the aura is applied in RoundCoordinator (see Warband section above).
};

// ─── Ability 12: Necromancy ────────────────────────────────────────────────
// OnDeath-adjacent (Mage, Healer, level requirement 5).
// While the Necromancy owner is alive, when a NEIGHBORING friendly minion
// (owner's side, battlefield position ± 1) dies in combat — from combat
// damage or from ability damage — the dead minion is temporarily replaced
// in its slot by a living Undead combat copy (UNDEAD_ATTACK / UNDEAD_HP).
// The Undead is TEMPORARY: it only ever exists in the combat copies and the
// combat order array, never in the persistent player state. When combat
// ends, the battlefield is restored from the pre-combat snapshot, which
// removes the Undead and brings back the original minion.
// The effect is applied by RoundCoordinator.applyNecromancy after every
// attack exchange (so the Undead can still fight in this combat).
// This executor is a no-op placeholder — the ability's presence is what matters.

const NECROMANCY_ID = NECROMANCY_ABILITY_ID;

const necromancyExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Necromancy section above.
};

// ─── Ability 13: Improved ──────────────────────────────────────────────────
// Passive (Undead, level requirement 0) — every Undead card starts with it.
// +IMPROVED_STAT_BONUS attack and +IMPROVED_STAT_BONUS HP for every PREVIOUS
// Undead that the OWNING player has played from a hand or summoned in
// combat this game (per-player counter — the opponent's plays/summons/feeds
// never count).
// The effect lives in RoundCoordinator, which owns the PER-PLAYER counters
// (undeadPlayedOrSummonedCountFor(playerId)) and applies the derived bonus:
//   - to hand/battlefield minions when their state is broadcast to clients
//     (GameSessionService builders), and
//   - to combat copies at combat start and after every Necromancy summon
//     (updateImprovedBuff), so mid-combat summons keep the owner's Undead
//     copies up to date.
// Persistent minion stats are NEVER modified — the bonus is recomputed from
// the owner's counter at read time, which is what makes it "continuously
// updated everywhere the Undead card exists".
// This executor is a no-op placeholder — the ability's presence is what matters.

const IMPROVED_ID = IMPROVED_ABILITY_ID;

const improvedExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Improved section above.
};

// ─── Ability 14: Gravedigger ───────────────────────────────────────────────
// Passive (Mage, Healer, level requirement 3).
// While a minion with this ability is on the battlefield, the Undead card is
// added to its owner's draw pool, so Undead can be drawn into the hand (and
// then played like any other card). The check lives in RoundCoordinator's
// round-draw step (getGravediggerExtraPool), which passes the Undead
// definition as a per-draw pool extension to CardDrawService.
// This executor is a no-op placeholder — the ability's presence is what matters.

const GRAVEDIGGER_ID = GRAVEDIGGER_ABILITY_ID;

const gravediggerExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Gravedigger section above.
};

// ─── Ability 15: Discount ──────────────────────────────────────────────────
// Passive (Bard, level requirement 2).
// While a minion with this ability is on the battlefield, buying a new card
// costs DISCOUNT_COST_REDUCTION less gold; multiple Discounts in play stack.
// The cost is clamped at GameConfig.cardPurchaseMinCost. Only minions in
// play count. The effect lives in GameSessionService.getCardPurchaseCost,
// the single authoritative place where the purchase cost is computed.
// This executor is a no-op placeholder — the ability's presence is what matters.

const DISCOUNT_ID = DISCOUNT_ABILITY_ID;

const discountExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: the cost reduction is applied in GameSessionService.getCardPurchaseCost.
};

// ─── Ability 16: Heirloom ───────────────────────────────────────────────────
// Passive (Warrior, Bard, Mage, level requirement 2).
// When the card is SOLD (from the hand or the battlefield), a random
// friendly minion on the battlefield permanently gains the sold card's
// current attack and HP. The effect lives in GameSessionService.sellMinion —
// the single authoritative sell path (the PlayerState model has no RNG, and
// all randomness must go through the injected RandomNumberService). If the
// random target itself has Teacher, the gain is redirected again per Teacher
// (see PlayerState.redirectTeacherStatGain).
// This executor is a no-op placeholder — the ability's presence is what matters.

const HEIRLOOM_ID = HEIRLOOM_ABILITY_ID;

const heirloomExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Heirloom section above.
};

// ─── Ability 17: Teacher ────────────────────────────────────────────────────
// Passive (Healer, level requirement 3).
// Permanent stat gains the Teacher receives are redirected: EVERY neighbor
// (battlefield position ± 1) receives the FULL amount and the Teacher
// receives nothing. Teacher neighbors pass the gain outward, never back to
// the unit they received it from (no infinite loops). A Teacher with no
// neighbors keeps the gain for itself. Keywords/abilities and healing are
// never redirected. The effect lives in PlayerState.redirectTeacherStatGain,
// invoked from PlayerState.upgradeMinion, RoundCoordinator
// .applyPermanentStatChanges and GameSessionService.sellMinion (Heirloom).
// This executor is a no-op placeholder — the ability's presence is what matters.

const TEACHER_ID = TEACHER_ABILITY_ID;

const teacherExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Teacher section above.
};

// ─── Ability 18: Greater Heal ──────────────────────────────────────────────
// OnAttack (Healer, Bard, level requirement 3).
// When this minion attacks, restores GREATER_HEAL_AMOUNT (6) HP to EVERY
// LIVING friendly minion on the owner's side — including the attacker
// itself (dead copies are skipped). Unlike Heal (neighbors only), this is
// a board-wide heal of the owner's side.
// Combat copies only (temporary — discarded after combat), capped at each
// target's initial HP at start of combat (same convention as Heal).
// Fortress targets double the heal, and the owner's Rune of Healing doubles
// it again (multiplicative). Acquisition: a minion may only LEARN this
// ability if it already has Heal; the upgrade removes Heal (see
// abilitiesReplacedByUpgrade in UpgradePath.ts), so both never coexist.
const GREATER_HEAL_ID = GREATER_HEAL_ABILITY_ID;

const greaterHealExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const combatCopies = context.combatCopies;

  if (!combatCopies) return; // No combat copies available (non-combat trigger)

  // Each target's heal is independent (amount computed from its own
  // current/initial HP), so healing in place while iterating is safe.
  // Targets with nothing to heal are skipped entirely (no event, no hook)
  // — same convention as Heal.
  for (const [instanceId, copy] of combatCopies) {
    if (!copy.isAlive) continue; // Skip dead minions

    // Fortress targets double the heal, and the owner's Rune of Healing
    // doubles it again (multiplicative).
    const currentHp = copy.currentHp;
    const initialHp = copy.initialHp;
    const rawHeal =
      GREATER_HEAL_AMOUNT * fortressHpMultiplier(copy.abilities) * healingRuneMultiplier(context, context.owner);

    // Over-feed (the owner's purchased rune — every target is friendly, so
    // the healer's owner is the target's owner): the heal is NOT capped at
    // the max HP — the overflow past the cap RAISES the temporary max HP
    // (the copy's heal cap) by the overflow for the rest of THIS combat
    // (same convention as the Heal executor). Grandmaster targets keep
    // their 1-HP cap — their HP gains are ignored.
    const overFeed =
      sideHasRune(context, context.owner, RUNE_OVERFEED_ID) &&
      !copy.abilities.includes(GRANDMASTER_ABILITY_ID);

    let actualHeal: number;
    if (overFeed) {
      actualHeal = rawHeal; // never capped
      copy.heal(rawHeal);
      const overflow = Math.max(0, copy.currentHp - initialHp);
      if (overflow > 0) {
        (copy as { initialHp: number }).initialHp += overflow;
      }
    } else {
      // Capped at initial HP (healing can never exceed max HP) without the rune.
      const maxHeal = initialHp - currentHp;
      if (maxHeal <= 0) continue; // Already at or above max
      actualHeal = Math.min(rawHeal, maxHeal);
      copy.heal(actualHeal);
    }

    // Achievement observation (Healing Voice convention): report the HP
    // actually restored (the uncapped amount under Over-feed) — the hook
    // is a no-op when absent. The DevServer only counts BARD sources, so a
    // healer's Greater Heal never feeds the bard achievement — a bard's does.
    context.abilityEffectHooks?.onHealApplied?.(instanceId, actualHeal);
  }
};

// ─── Ability 19: Buff Attack ───────────────────────────────────────────────
// OnAttack (Bard, level requirement 0).
// When this minion attacks, permanently adds BUFF_ATTACK_BONUS (3) attack
// to the LIVING minion immediately to the LEFT on the owner's side
// (battlefield position - 1 — the same "left neighbor" convention as Buff
// Vitality). Dead or missing neighbors are skipped.
// EVERY ATTACK: the buff applies on EVERY attack the owner makes (the
// user's rule — same per-attack semantics as Buff Vitality; a unit
// holding both applies each one on every attack).
// The gain is PERMANENT: it is recorded in context.permanentStatChanges
// (keyed by the target's instance ID) and applied to the persistent
// MinionInstance by RoundCoordinator.applyPermanentStatChanges AFTER the
// battlefield is restored from the pre-combat snapshot. Teacher
// redirection is applied there (and in-combat via the same deterministic
// cascade, so both resolutions always agree on the recipients).
// The in-combat portion raises the combat copy's attack so the buff is in
// effect from the target's NEXT attack in the SAME combat. The mid-combat
// ATK change reaches the UI as an 'aura' combat event (the RoundCoordinator
// snapshots copy attacks around the OnAttack window and emits an aura event
// for any changed copy — same convention as Warband).
// Fortress does NOT double the buff (it only doubles HP effects).
const BUFF_ATTACK_ID = BUFF_ATTACK_ABILITY_ID;

const buffAttackExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const combatCopies = context.combatCopies;

  if (!combatCopies) return; // No combat copies available (non-combat trigger)

  // Find the attacker's battlefield position
  const attackerPosition = eventData.attackerCopy?.battlefieldPosition
    ?? context.sourceMinion.battlefieldPosition;

  if (attackerPosition < 0) return; // Not on battlefield

  // Find the left neighbor (position - 1)
  const leftNeighborPos = attackerPosition - 1;

  let leftNeighborCopy: CombatMinionCopy | null = null;
  let leftNeighborInstanceId: string | null = null;
  for (const [instanceId, copy] of combatCopies) {
    if (copy.battlefieldPosition === leftNeighborPos) {
      leftNeighborCopy = copy;
      leftNeighborInstanceId = instanceId;
      break;
    }
  }

  if (!leftNeighborCopy || !leftNeighborCopy.isAlive) return; // Skip dead or missing neighbor
  if (!leftNeighborInstanceId) return;

  const attackAmount = BUFF_ATTACK_BONUS;

  // Record the PERMANENT attack increase keyed by the buff's TARGET. Teacher
  // redirection is applied later at the single choke point
  // (RoundCoordinator.applyPermanentStatChanges →
  // player.redirectTeacherStatGain), using the same deterministic cascade
  // as the in-combat portion below — combat positions are fixed, so both
  // resolutions always agree on the final recipients.
  if (context.permanentStatChanges) {
    // MERGE (spread existing) — see the Buff Vitality note: a unit holding
    // both buffs must keep BOTH permanent portions for the same target.
    const existing = context.permanentStatChanges.get(leftNeighborInstanceId) ?? {};
    context.permanentStatChanges.set(leftNeighborInstanceId, {
      ...existing,
      attackChange: (existing.attackChange ?? 0) + attackAmount,
    });
  }

  // In-combat portion: raise the attack of the combat copy that actually
  // holds the buff during THIS combat, so the target's next attack in the
  // same combat already deals the bonus. If the target has Teacher, the
  // Teacher receives NOTHING even during the combat: the same deterministic
  // cascade (PlayerState.resolveTeacherStatRedistribution) decides which
  // neighbor copies get the boost, matching the post-combat persistent
  // result.
  const originMinion = context.owner.minionInstances.get(leftNeighborInstanceId);
  const recipients = originMinion
    ? context.owner.resolveTeacherStatRedistribution(originMinion, 0, attackAmount)
    : [];

  if (recipients.length > 0) {
    for (const recipient of recipients) {
      const copy = context.combatCopies?.get(recipient.instanceId);
      // DEAD recipients get NOTHING (the Selfless/Teacher dead-neighbor
      // rule — see buffVitalityExecutor for the full note).
      if (!copy || !copy.isAlive) continue;
      copy.attack += recipient.atkGain;
    }
  } else {
    // No redirect (target lacks Teacher, or a Teacher with no neighbors
    // keeps it for itself — same target either way).
    leftNeighborCopy.attack += attackAmount;
  }
};

// ─── Ability 20: Grandmaster ─────────────────────────────────────────────
// Passive (Healer, Mage, level requirement 5).
// Board-state rule with NO combat effect: while a Grandmaster minion is ON
// the battlefield, the owner's level cap is extended by one per Grandmaster
// (stacking) — the checks live at every level-cap site via
// PlayerState.getMaxUpgrades (PlayerState.upgradeMinion, GameSessionService
// .upgradeMinion, BotService, UpgradeChoiceUI). Already-earned levels are
// never revoked (the cap only gates future upgrades). The companion 1-HP
// cap lives in MinionInstance (applyUpgrade clamp on acquisition + gainHp
// for every later HP gain), with combat-copy guards in the Buff Vitality
// executor and RoundCoordinator.applyRuneStatBuffs (Health rune).
// This executor is a no-op placeholder — the ability's presence is what matters.

const GRANDMASTER_ID = GRANDMASTER_ABILITY_ID;

const grandmasterExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Grandmaster section above.
};

// ─── Ability 21: DeathFeeder ───────────────────────────────────────────────
// Passive (Tank, level requirement 5).
// Every time this minion SURVIVES taking damage in combat it FEEDS: the
// OWNER's Improved counter (undeadPlayedOrSummonedCountFor(ownerId))
// advances by DEATHFEEDER_FEED_STACKS — exactly like the owner playing or
// summoning another Undead. The buff is CONSTRAINED TO THE OWNING PLAYER:
// only the owner's minions with Improved (their Undead cards) gain
// DEATHFEEDER_FEED_STACKS × IMPROVED_STAT_BONUS attack/HP, derived from the
// owner's counter — in hand/on the battlefield at broadcast time and in
// combat in place. The opponent's Improved minions are untouched. The tank
// itself gains NO stats; nothing is recorded in permanentStatChanges (the
// counter IS the persistence — it is per-player and monotonic, reset only
// in startMatch).
// The effect lives in RoundCoordinator at the four damage sites (normal
// exchange, OnAttack splash, OnDeath AoE, sudden-death tick) via
// _feedDeathFeeder: it advances the owner's counter (registerDeathFeederFeed
// (ownerId)), recomputes the derived buff on both sides (updateImprovedBuff
// — in practice only the owner's copies change) and streams the changes
// over the existing Improved channels (emitImprovedBuffEvents — aura +
// heal-style damage events, sourced from the tank that fed). Per damage
// instance: a copy hit twice in one exchange feeds twice; a copy that does
// not survive a hit feeds nothing for it.
// This executor is a no-op placeholder.
const DEATHFEEDER_ID = DEATHFEEDER_ABILITY_ID;

const deathfeederExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the DeathFeeder section above.
};

// ─── Ability 22: Exploding ────────────────────────────────────────────────
// OnDeath-adjacent (Mage, level requirement 3).
// When a FRIENDLY minion dies in combat (from any death source — combat
// damage, ability damage, sudden death, an explosion of the chain) — its
// OWN death included — an explosion happens AT THE DEAD MINION: every
// LIVING friendly Exploding copy deals EXPLODING_DAMAGE (3) to ALL minions
// (both sides, every living copy — the snapshot-then-damage convention of
// Explosive), and the dead minion's corpse BOOMs (one BOOM per contributing
// source; the dead copy's OWN Exploding is a contributing source too —
// own-death trigger: a minion dies WITH Exploding, it explodes, no matter
// how it died). "Explosive triggered by any friendly death".
// LIVING SOURCES RE-FIRE: a living Exploding copy explodes for EVERY
// friendly death it witnesses — a chain of deaths (A dies → explosion kills
// C → C's explosion kills F → ...) keeps detonating: the explosion chain
// (RoundCoordinator._runExplosionChain) resolves the deaths in BFS order —
// A dies (and explodes) → save the deaths (C and E) → resolve C (it kills
// F) → resolve E (it kills K) → F → K → ... Dead minions can't die again
// (blasts hit the living only) and a death is resolved once. Resurrections
// never happen INSIDE the chain: Resurrect is deferred to after the whole
// exchange (the Resurrect special case — "if there is a Resurrect, resolve
// it last"), so a death stays a death until the chain finishes (a minion
// that dies WITH Exploding always explodes, even when it is resurrected
// afterwards — it DID die) and a resurrected copy is a FRESH living minion
// for the next exchange (a later death there is a NEW death, fully
// re-resolved by that exchange's chain).
// THE CHAIN SPANS EXPLOSIVE TOO: a death that never had its OnDeath window
// (blast kill, splash kill, sudden-death kill) runs its window inside the
// chain — an Explosive minion killed by an explosion also explodes (its
// OnDeath blast), and its blast's kills continue the chain. Explosive and
// Exploding therefore chain together, on both sides of the board.
// FX: the BOOM ('ability' event) plays at the DEAD minion's position (the
// corpse element); every LIVING Exploding source whose passive fired gets
// the red shimmer ('explodingShimmer' event — around its edges, one event
// per death with all living sources) so the player sees which minion's
// passive caused the boom. The OnDeath window skips 'exploding' so the BOOM
// FX is never doubled.
// Tokens (Necromancy Undead summons) are not cards and do not count as the
// friendly death (same convention as the rune death effects).
// The effect lives in RoundCoordinator.applyExploding (the per-exchange
// explosion chain, run after the OnDeath windows + sudden-death tick and
// before the rune death effects / Necromancy scan so its own kills feed
// them the same exchange — exactly like Explosive's). The AoE damage
// follows the ability-damage convention: simultaneous damage/death events +
// per-user kill credit for ENEMY non-token victims + KILL GOLD for those
// same victims + Mountain observation + DeathFeeder feeds, but NO
// Exploding-Heartbeat credit (that achievement is the Explosive ability's;
// a minion that learned Exploding no longer carries Explosive). All of the
// chain's events stream BEFORE the next exchange's attack event, so the
// shared CombatAnimationTiming plays every BOOM before the next minion
// attacks.
// Acquisition: only learnable when the minion ALREADY has Explosive; the
// upgrade removes Explosive (abilitiesReplacedByUpgrade), so both never
// coexist.
// This executor is a no-op placeholder.
const EXPLODING_ID = EXPLODING_ABILITY_ID;

const explodingExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Exploding section above.
};

// ─── Ability 23: SoulLink ─────────────────────────────────────────────────
// OnDeath-adjacent (Tank, level requirement 3).
// When this minion DIES in combat (from any death source — combat damage,
// ability damage, sudden death), a random OTHER living friendly minion gains
// the deceased's max HP (the dead copy's initialHp at scan time — combat-only
// buffs included) as a TEMPORARY HP buff: the recipient's current HP AND its
// max HP (heal cap) both rise by the grant for the rest of THIS combat only.
// The buff is applied to the combat copies in place (never to
// permanentStatChanges), so battlefield restoration discards it. A recipient
// with Fortress doubles the grant (target-side convention — same as Heal /
// Buff Vitality); a recipient with Grandmaster can never hold it and is
// excluded from the recipient pool. If no other friendly minion is alive, the
// soul is lost (Heirloom convention). Randomness goes through the match RNG
// (deterministic with the same seed).
// The effect lives in RoundCoordinator.applySoulLink (the per-exchange
// SoulLink-death scan — alive-at-exchange-start × now-dead, run after
// Exploding and before the rune death effects / Necromancy scan, so the
// corpse copy is still in the map and its max HP is readable). Resurrect is
// DEFERRED to after the whole exchange (the Resurrect special case — "if
// there is a Resurrect, resolve it last"), so a copy resurrected at the end
// of the exchange was a corpse at scan time and GRANTS for that death (it
// DID die); it is a fresh living minion for the next exchange, where a later
// death grants again (per death). Only Phase 1/2 revivals (Lifesteal /
// Killer heals) still count as "never died" (they precede Phase 3's death
// finalization).
// The 'ability' stream event is emitted by applySoulLink (the OnDeath window
// skips it for exchange deaths — single source, like Exploding's skip).
// This executor is a no-op placeholder.
const SOULLINK_ID = SOULLINK_ABILITY_ID;

const soulLinkExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the SoulLink section above.
};

// ─── Ability 24: Blessed ──────────────────────────────────────────────────
// Passive (Tank, level requirement 3).
// The FIRST time this minion takes damage in combat — from ANY source — it
// GAINS that much HP instead of taking the damage (a 5-HP Blessed tank hit
// for 3 ends the exchange at 8). The gain is a temporary HP buff: the copy's
// current HP AND its max HP (heal cap) both rise by the gained amount for the
// rest of THIS combat only. A Blessed copy with Fortress doubles the gain
// (ability-HP-effect convention). The per-combat charge lives on the combat
// copy (blessedUsedInCombat — copies are rebuilt every round, so the charge
// resets automatically each combat). Non-positive "damage" never consumes the
// charge; later hits in the same combat apply normally. Nothing is recorded
// in permanentStatChanges — the buff dies with the combat copies.
// The effect lives in CombatMinionCopy.takeDamage — the single damage choke
// point (normal exchange, counter-attack, Blast splash, Explosive/Exploding
// AoE, sudden-death tick), which is what makes "from any source" hold without
// per-site wiring. This executor is a no-op placeholder.
const BLESSED_ID = BLESSED_ABILITY_ID;

const blessedExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Blessed section above.
};

// ─── Ability 25: Master Buff ────────────────────────────────────────────────
// OnAttack (Bard, level requirement 4).
// When the owner attacks, EACH LIVING neighbor (battlefield position ± 1,
// owner's side) permanently gains MASTER_BUFF_STAT_BONUS (5) HP AND attack —
// the attack repeats, the buff repeats (each attack buffs again, stacking
// per attack like the Buff Vitality / Buff Attack triggers it supersedes).
//
// Conventions (same as the two abilities it replaces):
//  - DEAD or missing neighbors are skipped (only "alive neighbors" are
//    buffed — rechecked at trigger time, not at combat start);
//  - the gain is PERMANENT: recorded in context.permanentStatChanges keyed
//    by the TARGET's instance ID, applied to the persistent instances by
//    RoundCoordinator.applyPermanentStatChanges after battlefield
//    restoration — and applied to the combat copies in combat, so the
//    buff is in effect from the neighbor's NEXT attack in the same combat
//    (the mid-combat ATK change reaches the UI through the OnAttack aura
//    scan, the Buff Attack convention; the HP raise bumps currentHp AND
//    initialHp, the Buff Vitality convention);
//  - Fortress neighbors double the HP PORTION only (fortressHpMultiplier —
//    Fortress doubles HP effects, never attack);
//  - Grandmaster neighbors can never gain HP (the HP portion is skipped for
//    them — the Buff Vitality convention), but they still receive the
//    ATTACK portion (the Rune of Light convention);
//  - Teacher neighbors redirect BOTH portions to their own neighbors (the
//    apply-then-redirect convention: the in-combat portion resolves through
//    resolveTeacherStatRedistribution now, the permanent portion through
//    redirectTeacherStatGain in applyPermanentStatChanges later — combat
//    positions are fixed, so both resolutions always agree);
//  - the in-combat portion applies to the combat copies (heal caps respect
//    the new max HP); the permanent map drives the post-combat result.
const MASTER_BUFF_ID = MASTER_BUFF_ABILITY_ID;

const masterBuffExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const combatCopies = context.combatCopies;

  if (!combatCopies) return; // No combat copies available (non-combat trigger)

  // Find the attacker's battlefield position
  const attackerPosition = eventData.attackerCopy?.battlefieldPosition
    ?? context.sourceMinion.battlefieldPosition;

  if (attackerPosition < 0) return; // Not on battlefield

  // Both living neighbors (position - 1 and + 1, owner's side). Unlike the
  // two abilities it replaces (left neighbor only), Master Buff covers BOTH.
  for (const neighborPos of [attackerPosition - 1, attackerPosition + 1]) {
    let neighborCopy: CombatMinionCopy | null = null;
    let neighborInstanceId: string | null = null;
    for (const [instanceId, copy] of combatCopies) {
      if (copy.battlefieldPosition === neighborPos) {
        neighborCopy = copy;
        neighborInstanceId = instanceId;
        break;
      }
    }

    if (!neighborCopy || !neighborCopy.isAlive) continue; // Skip dead or missing neighbor
    if (!neighborInstanceId) continue;

    const isGrandmasterTarget = neighborCopy.abilities.includes(GRANDMASTER_ABILITY_ID);

    // Fortress neighbors double the HP portion (the ATK portion is never
    // doubled — Fortress only doubles HP effects). Grandmaster targets can
    // never gain HP (the portion is skipped entirely for them), but they
    // still receive the attack gain (Rune of Light convention).
    const hpAmount = isGrandmasterTarget
      ? 0
      : MASTER_BUFF_STAT_BONUS * fortressHpMultiplier(neighborCopy.abilities);
    const atkAmount = MASTER_BUFF_STAT_BONUS;

    // Record the PERMANENT gain keyed by the buff's TARGET (the single
    // record both the in-combat portion and the post-combat application
    // flow from). Teacher redirection is applied later at the choke point
    // (RoundCoordinator.applyPermanentStatChanges →
    // player.redirectTeacherStatGain), using the same deterministic cascade
    // as the in-combat portion below — combat positions are fixed, so both
    // resolutions always agree on the final recipients.
    if (context.permanentStatChanges) {
      const existing = context.permanentStatChanges.get(neighborInstanceId) ?? {};
      context.permanentStatChanges.set(neighborInstanceId, {
        hpChange: (existing.hpChange ?? 0) + hpAmount,
        attackChange: (existing.attackChange ?? 0) + atkAmount,
      });
    }

    // In-combat portion: raise the currentHp AND initialHp (readonly — type
    // assertion, the established cast pattern) and the attack of the combat
    // copy that actually holds the buff during THIS combat. If the target
    // has Teacher, the Teacher receives NOTHING even during the combat: the
    // same deterministic cascade
    // (PlayerState.resolveTeacherStatRedistribution) decides which neighbor
    // copies get the boost, matching the post-combat persistent result.
    const originMinion = context.owner.minionInstances.get(neighborInstanceId);
    const recipients = originMinion
      ? context.owner.resolveTeacherStatRedistribution(originMinion, hpAmount, atkAmount)
      : [];

    if (recipients.length > 0) {
      for (const recipient of recipients) {
        const copy = context.combatCopies?.get(recipient.instanceId);
        // DEAD recipients get NOTHING (the Selfless/Teacher dead-neighbor
        // rule — see buffVitalityExecutor for the full note).
        if (!copy || !copy.isAlive) continue;
        // Grandmaster recipient (end of a Teacher cascade): its HP can never
        // exceed 1 — the HP boost is ignored, exactly like the persistent
        // side (gainHp clamps there too). The ATK portion still applies.
        const isGrandmasterRecipient = copy.abilities.includes(GRANDMASTER_ABILITY_ID);
        // Achievement observation (Healthy Undead): the permanent max-HP
        // boost applies to every ACTUAL recipient (post-Teacher-redirect).
        if (recipient.hpGain > 0) {
          context.abilityEffectHooks?.onVitalityBoostApplied?.(recipient.instanceId, recipient.hpGain);
        }
        if (!isGrandmasterRecipient) {
          copy.currentHp += recipient.hpGain;
          (copy as { initialHp: number }).initialHp += recipient.hpGain;
        }
        copy.attack += recipient.atkGain;
      }
    } else {
      // No redirect (target lacks Teacher, or a Teacher with no neighbors
      // keeps it for itself — same target either way).
      if (!isGrandmasterTarget) {
        neighborCopy.currentHp += hpAmount;
        (neighborCopy as { initialHp: number }).initialHp += hpAmount;
        context.abilityEffectHooks?.onVitalityBoostApplied?.(neighborInstanceId, hpAmount);
      }
      neighborCopy.attack += atkAmount;
    }
  }
};

// ─── Ability 26: WarTribe ───────────────────────────────────────────────────
// Passive (Bard, level requirement 4).
// At the start of EACH combat, every minion on the owner's battlefield
// PERMANENTLY gains WARTRIBE_ATK_BONUS (3) attack per WarTribe minion in
// play (stacking). The gain is applied to the PERSISTENT instances BEFORE
// the pre-combat snapshot (RoundCoordinator.applyWarTribe — the Rune of
// Light convention), so it survives combat restoration and accumulates in
// every combat. Teacher redirection applies (the apply-then-redirect
// convention). This executor is a no-op placeholder, same pattern as
// Warband's.
const WARTRIBE_ID = WARTRIBE_ABILITY_ID;

const warTribeExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the WarTribe section above.
};

// ─── Ability 27: Holy Shield ───────────────────────────────────────────────
// Passive (Tank, Warrior, level requirement 3).
// Damage dealt to this minion by an UNDEAD-class unit is HALVED (rounded
// up) before it is applied — an 8-ATK Undead deals 4, a 7-ATK Undead deals
// 4, a 1-ATK Undead deals 1. The reduction is a damage rule at the single
// damage choke point (CombatMinionCopy.takeDamage): the damage source's
// class travels with each takeDamage call (exchange hit + counter-attack,
// Blast splash from an Undead attacker, Explosive/Exploding blasts from an
// Undead source). Sourceless damage (the sudden-death tick) and damage from
// non-Undead units are never reduced. There is no executor — the presence
// of the ability ID is what takeDamage checks. When the reduction actually
// applies, the copy queues a 'holy_shield' trigger that the
// RoundCoordinator drains into the combat stream (the client's blue shield
// wave at the card).
const HOLY_SHIELD_ID = HOLY_SHIELD_ABILITY_ID;

const holyShieldExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Holy Shield section above.
};

// ─── Ability 28: Second Chance ─────────────────────────────────────────────
// Passive (Warrior, Bard, Mage, Healer, Tank, Undead, level requirement 6).
// The FIRST time this minion would take LETHAL damage in combat (from ANY
// source — every damage site routes through CombatMinionCopy.takeDamage),
// the damage is NEGATED BEFORE it is applied and the minion is FULLY
// HEALED instead (current HP = the combat copy's initialHp, the max HP at
// that moment — combat buffs included). Because the damage is ignored
// before it lands, the copy never dies from the hit: no death event, no
// kill credit or kill gold, no OnKill/OnDeath dispatches, no resurrection
// observation (it was never a corpse). Non-lethal hits never consume the
// per-combat charge (secondChanceUsedInCombat — copies are rebuilt every
// round, so it resets each combat); a later lethal hit kills normally.
// Blessed keeps precedence over the first positive hit it takes (checked
// first in takeDamage): it converts that hit into an HP gain, and Second
// Chance covers the later lethal hits.
// ACQUISITION: only learnable by leveling PAST the base maximum level —
// which only exists while a friendly Grandmaster minion is on the
// battlefield (the Grandmaster level-extension effect). The rule lives in
// the shared eligibility check (isAbilityEligibleForUpgrade,
// grandmasterCount context). There is no executor — the intercept lives in
// takeDamage. When it triggers, the copy queues a 'second_chance' trigger
// that the RoundCoordinator drains into the combat stream (the client's
// golden wing burst at the card).
const SECOND_CHANCE_ID = SECOND_CHANCE_ABILITY_ID;

const secondChanceExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Second Chance section above.
};

// ─── RuneKeeper / RuneFeast (not yet obtainable) ───────────────────────────
// Both are PASSIVE: the effect lives in the rune purchase path
// (GameSessionService.buyRune + BotService, via the shared
// RunePurchaseRules helpers, which read the battlefield for these ability
// IDs). The executors are no-op placeholders — the presence of the ability
// ID is what matters (the Draw/Warband convention). Their catalogue
// definitions carry an empty `types` array, so no class can acquire them
// yet; the mechanics are in place for when a minion gains them.
const RUNE_KEEPER_ID = RUNE_KEEPER_ABILITY_ID;
const RUNE_FEAST_ID = RUNE_FEAST_ABILITY_ID;

const runeKeeperExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // This executor is a no-op placeholder — the per-game rune cap is
  // computed in the purchase path (getEffectiveRuneCap).
};

const runeFeastExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // This executor is a no-op placeholder — the 25% rune discount is applied
  // in the purchase path (getRuneGoldCost).
};

// ─── Ability 30: Toxic Cloud ───────────────────────────────────────────────
// Passive (Blightcaster, level requirement 3).
// At the start of each ENEMY attack turn (the side whose turn it is), while
// at least one Toxic Cloud holder is ALIVE, ALL LIVING minions of that side
// take TOXIC_CLOUD_DAMAGE × (living holders) damage — the clouds STACK, but
// the cloud triggers only ONCE per turn (a single damage instance per
// minion, one green cloud FX over the whole enemy battlefield). The holder
// cannot be on the poisoned side (the cloud always hits the holder's
// enemies), so the holder never takes its own cloud.
// The effect lives in RoundCoordinator.applyToxicCloudTick (the per-turn
// tick at the top of the combat loop, before the attacker is chosen — a
// minion the cloud kills simply never attacks; a wiped side ends the
// combat). The damage routes through takeDamage (Mark +3, Blessed
// conversion, Second Chance negation and the Shared Pain accumulation all
// apply), and follows the ability-damage convention: simultaneous
// damage/death events + kill credit for enemy non-token victims, no kill
// gold. This executor is a no-op placeholder (the Warband/Blessed
// convention).
const TOXIC_CLOUD_ID = TOXIC_CLOUD_ABILITY_ID;

const toxicCloudExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Toxic Cloud section above.
};

// ─── Ability 31: Blunt ─────────────────────────────────────────────────────
// OnAttack (Blightcaster, level requirement 1).
// When the owner attacks, EVERY LIVING ENEMY combat copy's attack is
// reduced by BLUNT_ATK_REDUCTION (1), floored at 0. Each attack dulls
// again (stacking per attack — the Master Buff per-attack convention).
// TEMPORARY by construction: the executor touches the combat copies only
// (never context.permanentStatChanges), so battlefield restoration
// discards the reduction — it does not persist after battle.
// Dead enemies are skipped (a corpse's attack is irrelevant); the
// mid-combat ATK change reaches the UI via the defender-side aura event
// the RoundCoordinator emits after the OnAttack window.
const BLUNT_ID = BLUNT_ABILITY_ID;

const bluntExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const opposingCopies = context.opposingCombatCopies;
  if (!opposingCopies) return; // No combat copies available (non-combat trigger)

  for (const copy of opposingCopies.values()) {
    if (!copy.isAlive) continue; // dead enemies keep their stats
    copy.attack = Math.max(0, copy.attack - BLUNT_ATK_REDUCTION);
  }
};

// ─── Ability 32: Mark ──────────────────────────────────────────────────────
// OnAttack (Blightcaster, level requirement 1).
// When the owner attacks, the DEFENDER it attacked is MARKED (living
// defenders only — a defender the exchange already killed is not marked:
// the mark is combat-scoped and a corpse takes no damage). A marked copy
// takes MARK_EXTRA_DAMAGE (3) extra damage on EVERY damage instance it
// takes — the +3 is applied in CombatMinionCopy.takeDamage, the single
// damage choke point, so it works against any damage source (exchange,
// splash, blast, sudden death, the Toxic Cloud tick, Shared Pain shares).
// The mark is a non-stacking flag (the first mark is enough — the ability
// does not stack per Mark holder). It is combat-scoped by construction
// (combat copies are rebuilt every round). The RoundCoordinator streams a
// 'mark' combat event when the defender newly gains the mark (the client's
// continuous black-particle emitter at the marked minion).
const MARK_ID = MARK_ABILITY_ID;

const markExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const defenderCopy = eventData.defenderCopy;

  if (!defenderCopy) return; // No combat copy available (non-combat trigger)
  if (!defenderCopy.isAlive) return; // a corpse is not marked (see above)

  // Non-stacking flag: re-marking an already marked copy is a no-op.
  defenderCopy.marked = true;
};

// ─── Ability 33: Shared Pain ───────────────────────────────────────────────
// Passive (Blightcaster, level requirement 4).
// ACQUISITION: only learnable when the minion ALREADY has Mark (the one-way
// gate in isAbilityEligibleForUpgrade — Mark is NOT removed, so the holder
// marks AND shares).
// EFFECT: every time the holder TAKES damage (the HP actually lost —
// overkill not absorbed; Blessed conversions and Second Chance negations
// are not damage taken), EVERY LIVING ENEMY MARKED copy takes that same
// amount (the mark's +3 applies to the share too). Re-entrancy guard:
// shared pain does not re-trigger the target's own Shared Pain (one
// propagation level per damage instance — see TakeDamageOptions).
// Accumulation: CombatMinionCopy.takeDamage queues the actual HP loss in
// pendingSharedPain (the copy has no access to the session); the effect
// lives in RoundCoordinator._drainSharedPain, called at every damage site
// (exchange, OnAttack window, OnDeath window, the explosion-chain blasts,
// the sudden-death tick, the Toxic Cloud tick). The 'sharedPain' stream
// event carries the holder + the marked victims (the client's red
// expanding particle line), followed by the simultaneous damage/death
// events. Ability-damage convention: kill credit for enemy non-token
// victims + kill gold for the kills via the centralized death-gold rule
// (RoundCoordinator._awardDeathGold — the share is the enemy side's doing,
// so each kill pays the holder's owner). This executor is a no-op
// placeholder.
const SHARED_PAIN_ID = SHARED_PAIN_ABILITY_ID;

const sharedPainExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Shared Pain section above.
};

// ─── Frenzy (Runekeeper) — combat aura (no-op executor) ───────────────────
// The effect is a self-aura on the holder: while its current HP is below
// HALF of its max HP (initialHp) it attacks with +FRENZY_ATK_BONUS. The
// RoundCoordinator recomputes it at combat start and at the top of every
// attack turn (updateFrenzyAura — the Warband aura convention: the tracked
// per-copy bonus is delta-adjusted onto copy.attack and the changes stream
// as the generic 'aura' event). TEMPORARY by construction: combat copies
// only, nothing touches permanentStatChanges.
const FRENZY_ID = FRENZY_ABILITY_ID;

const frenzyExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Frenzy section above.
};

// ─── Random Evolution (Runekeeper) — upgrade-time grant (no-op executor) ──
// Learning this ability NEVER adds 'random_evolution' to the minion — the
// upgrade resolves to a random KEYWORD from any class (the shared
// randomEvolutionPool / resolveRandomEvolutionGrant in UpgradePath, rolled
// server-side with the authoritative RNG and locked by the pre-selection
// convention). The option is offered only when no other ability is
// eligible (the getEligibleAbilityUpgrades fallback). In combat the holder
// fights with the keyword it was granted — this executor is a no-op
// registered for completeness.
const RANDOM_EVOLUTION_ID = RANDOM_EVOLUTION_ABILITY_ID;

const randomEvolutionExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Random Evolution section above.
};

// ─── Strengthen (Buffer) — combat aura (no-op executor) ───────────────────
// While a Strengthen holder is alive, EVERY living friendly combat copy
// gains +STRENGTHEN_STAT_BONUS attack AND +STRENGTHEN_STAT_BONUS HP (the
// holders stack — two living Strengthen minions = +6/+6, the Warband
// per-copy stacking convention). The RoundCoordinator recomputes the aura
// at combat start and before every attack (updateStrengthenAura — the
// Warband aura convention: the per-copy bonus tracked in the session is
// delta-adjusted onto the copy's attack / current HP / max HP, and the
// changes stream as the generic 'aura' event — Strengthen's entries carry
// the new attack AND HP). Removal rules: the ATK drop is floored at 0 and
// the HP drop lowers current AND max HP but can never kill a copy (HP is
// floored at 1). Grandmaster copies take the ATK portion only (the
// Grandmaster "never gains HP" convention). TEMPORARY by construction:
// combat copies only, nothing touches permanentStatChanges (does not
// persist after battle).
const STRENGTHEN_ID = STRENGTHEN_ABILITY_ID;

const strengthenExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Strengthen section above.
};

// ─── Blessing (Buffer) — combat lifesteal aura (no-op executor) ──────────
// While at least one Blessing holder is ALIVE on the attacker's side, EVERY
// friendly attacking copy heals for BLESSING_LIFESTEAL_BONUS per attack
// (the holders stack — two living Blessing minions = +4, the Warband
// per-copy stacking convention) — on TOP of a Lifesteal ability's
// damage-based heal when both are present. NO RESURRECTION: the heal is
// applied after the exchange's simultaneous damage and skips a corpse (the
// Lifesteal executor's isAlive guard — a 2-HP minion attacking a 10-ATK
// unit is 2 - 10 + 2 = -6 and stays dead). The count is read live at attack
// time, so a holder that died earlier simply stops granting ("if this
// minion dies the effect is removed"). Fortress (recipient) and the Rune
// of Healing (owner) double it, multiplicative — the exact Lifesteal
// multipliers. The effect lives in RoundCoordinator.applyBlessingLifesteal
// (right after the OnAttack window, before its HP-diff scan — the heal
// streams as the usual heal-style damage event).
const BLESSING_ID = BLESSING_ABILITY_ID;

const blessingExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Blessing section above.
};

// ─── Looter (Warrior, Runekeeper — level requirement 5) ────────────────────
// Passive — the effect lives in the RoundCoordinator's death-gold sites
// (the centralized _awardDeathGold rule: every enemy minion that dies in
// combat awards its level in gold to the player who killed it, plus
// LOOTER_GOLD_PER_DEATH per LIVING Looter on the recipient's side — the
// holders stack, the count is read live at death time). No-op executor,
// the Warband / Toxic Cloud convention.
const LOOTER_ID = LOOTER_ABILITY_ID;

const looterExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Looter section above.
};

// ─── Brute (Warrior, Mage — level requirement 2; Tank no longer offered) ──
// The effect lives in RoundCoordinator._applyBruteOnAttack (called in
// runCombatToCompletion after the 'attack' event, before
// applySimultaneousDamage): every time the holder attacks, its attack is
// doubled and its HP (current AND max) is halved (floored at BRUTE_HP_FLOOR,
// never below 1) — BEFORE the exchange damage is dealt/taken, combat copy
// only (temporary by construction, nothing persists after battle). The
// change streams as one 'aura' event (attack + hp + maxHp — the Strengthen
// shape). No-op executor.
const BRUTE_ID = BRUTE_ABILITY_ID;

const bruteExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Brute section above.
};

// ─── Freeze (Mage, Bard — level requirement 3) ────────────────────────────
// The effect lives in the RoundCoordinator: applyFreeze (called after the
// exchange's damage event — the FREEZE_TARGET_CHANCE / FREEZE_NEIGHBOR_
// CHANCE roll on the seeded match RNG
// freezes the living target, streaming a 'freeze' event) and the skip in
// runCombatToCompletion (a frozen attacker is unfrozen and skips its
// attack, streaming a 'freezeSkip' event). No-op executor.
const FREEZE_ID = FREEZE_ABILITY_ID;

const freezeExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Freeze section above.
};

// ─── Glasscannon (Swarmer — level requirement 0) ───────────────────────────
// "This minion dies after it has attacked." A REAL OnAttack executor:
// when the holder is the attacker of an exchange, after the exchange's
// simultaneous damage has landed, the holder's combat copy is KILLED
// (currentHp set to 0). A copy the exchange already dropped to 0 HP (a
// lethal counter-attack) is a corpse already — killing it again is a no-op
// (the death is the same death: one death event, no double bookkeeping —
// the per-exchange death pipeline finalizes the corpse exactly once).
// The death is then resolved by the normal phased death finalization (the
// death event; kill gold / kill credit are excluded by the Swarmer token
// rules — see the Swarmer section in AbilityConstants; the OnDeath window
// is empty: Glasscannon is the Swarmer's only ability and it is an
// OnAttack trigger, not an OnDeath one).
const GLASSCANNON_ID = GLASSCANNON_ABILITY_ID;

const glasscannonExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  const eventData = context.eventData as OnAttackEventData;
  const attackerCopy = eventData.attackerCopy;

  if (!attackerCopy) return; // No combat copy available (non-combat trigger)

  // The holder dies after it has attacked — even if it survived the
  // counter-attack (a 1-HP Swarmer hitting a 0-ATK target). A corpse the
  // exchange already made stays a corpse (the assignment is idempotent).
  attackerCopy.currentHp = 0;
  attackerCopy.isAlive = false;
};

// ─── SwarmLord (Mage/Blightcaster — level requirement 5) ───────────────────
// "When this minion dies, it fills your board with Swarmers that all attack
// immediately." No-op executor (the passive convention — like Necromancy /
// Exploding / SoulLink): the effect lives in the RoundCoordinator's
// _resolveSwarmLordBursts, which runs at the END of every attack exchange
// (after ALL of the exchange's death effects — OnDeath windows, the
// explosion chain, SoulLink, the rune death effects, Necromancy, the grief
// passes, the deferred Resurrects — "all on-deaths are resolved first")
// and just before the outcome check. The per-death scan (alive at exchange
// start × now a corpse × carries SwarmLord) fills the holder's board with
// Swarmer tokens (one per empty slot — the board ends FULL) and runs the
// swarm burst: every spawned Swarmer attacks once (full exchange, left to
// right) BEFORE the opposing side's next turn, then dies of Glasscannon.
const SWARMLORD_ID = SWARMLORD_ABILITY_ID;

const swarmlordExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the SwarmLord section above.
};

// ─── Treasure (Items, level 0) ─────────────────────────────────────────────
// The KILLER of a Treasure-carrying item gains TREASURE_GOLD_REWARD (25)
// gold on top of the level kill gold. The effect lives in the centralized
// death-gold engine (RoundCoordinator._awardDeathGold — folded into the
// same killGold event), so every death source pays it and the friendly-
// fire rule applies unchanged. The executor is a no-op.
const treasureExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Treasure section above (RoundCoordinator._awardDeathGold).
};

// ─── Life-full (Items, level 0) ────────────────────────────────────────────
// The KILLER of a Life-full-carrying item instantly gains
// LIFE_FULL_LIFE_BONUS (1) life. The effect lives in the centralized
// death-gold engine (RoundCoordinator._awardDeathGold — the recipient is
// the same player the kill gold goes to). The executor is a no-op.
const lifeFullExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Life-full section above (RoundCoordinator._awardDeathGold).
};

// ─── SwarmNest (Mage/Blightcaster — level requirement 6) ──────────────────
// "Your Swarmers spawn without the Fragile keyword." No-op executor (the
// passive convention): the effect lives in the RoundCoordinator's swarmer
// spawn (_runSwarmLordBurst reads the holder's abilities and drops the
// Fragile (Glasscannon) base ability from its Swarmer tokens — they
// survive their one burst attack and sit on the board as 1-HP minions).
const swarmnestExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the SwarmNest section above.
};

// ─── Spellmaker / SpellMaster (Spellmaker — level requirements 1 / 5) ─────
// Passive draw-pile keywords — no-op executors (the passive convention):
// the effect lives in the draw services (CardDrawService.
// drawCardForPlayerSpellPower counts the holder via
// PlayerState.countSpellPowerOnBattlefield: 1×Spellmaker, 2×SpellMaster).
const spellmakerExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Spellmaker section above.
};

const spellmasterExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the SpellMaster section above.
};

const repeaterExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Repeater section above (the effect lives in the
  // RoundCoordinator's applyRepeaterCasts).
};

// ─── Task 192: LastWish + Swift ─────────────────────────────────────────────

const lastWishExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  // The buff is COMBAT-SCOPED: combat copies only (the battlefield
  // restores from the pre-combat snapshot after combat). Every LIVING
  // friendly copy gains +5/+5 at the moment of the death (the dead holder
  // is skipped; copies that die later in the combat kept the buff while
  // they lived). currentHp AND the combat max-HP cap (initialHp) rise,
  // exactly like the other in-combat HP buffs (Blessed / Buff Vitality).
  const copies = context.combatCopies;
  if (!copies) return; // non-combat death — no combat to last through
  for (const copy of copies.values()) {
    if (!copy.isAlive) continue;
    if (copy.instanceId === context.sourceMinion.instanceId) continue;
    copy.attack += LASTWISH_STAT_BONUS;
    copy.currentHp += LASTWISH_STAT_BONUS;
    (copy as { initialHp: number }).initialHp += LASTWISH_STAT_BONUS;
  }
};

const swiftExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: the double attack lives in the RoundCoordinator's combat loop
  // (the post-exchange swift re-attack check + CombatSequence.
  // queueImmediateReattack) — see the SWIFT section there.
};

// ─── LifeBurst (Healer) — task 206 ─────────────────────────────────────────
// OnDeath: EVERY minion on the board — the holder's side AND the
// opposing side — is healed for LIFEBURST_HEAL_AMOUNT (capped at each
// copy's combat max HP, `initialHp` — the in-combat HP cap convention,
// exactly like the other heals). Combat-scoped: combat copies only
// (non-combat deaths see no combat copies and no-op). BOTH sides come
// from the OnDeath event data's allCombatCopies (the dispatcher does NOT
// populate context.opposingCombatCopies for OnDeath — that field is an
// OnAttack convention; the direct-dispatch fallback reads
// context.combatCopies). The per-target heal rides the standard
// negative-damage events the death window streams; the board-wide
// 'lifeBurst' FX event is streamed by the RoundCoordinator (it has the
// pre/post HP diff for both sides).
const lifeBurstExecutor: AbilityExecutor = (context: AbilityExecutionContext) => {
  // Rune of Healing (task 209): the holder's side's rune DOUBLES the heal
  // (the established healing-rune multiplier, multiplicative convention).
  const amount = LIFEBURST_HEAL_AMOUNT * healingRuneMultiplier(context, context.owner);
  // Over-Feed (task 209): the holder's side's rune lets the heal OVER-HEAL
  // — but only OWN minions (the enemy side stays capped; the overflow
  // raises the temporary max HP for the rest of this combat, exactly like
  // the Heal / Greater Heal over-feed convention). Grandmaster recipients
  // keep their 1-HP cap (the HP-gain clamp — no over-feed).
  const overFeed = sideHasRune(context, context.owner, RUNE_OVERFEED_ID);
  const healFrom = (copies: Map<string, CombatMinionCopy> | undefined, allowOverFeed: boolean) => {
    if (!copies) return;
    for (const copy of copies.values()) {
      if (!copy.isAlive) continue;
      const initialHp = (copy as unknown as { initialHp: number }).initialHp;
      const canOverfeed =
        allowOverFeed && overFeed && !copy.abilities.includes(GRANDMASTER_ABILITY_ID);
      if (canOverfeed) {
        copy.heal(amount); // never capped — the overflow raises the cap
        const overflow = Math.max(0, copy.currentHp - initialHp);
        if (overflow > 0) {
          (copy as unknown as { initialHp: number }).initialHp += overflow;
        }
      } else {
        const healed = Math.min(initialHp - copy.currentHp, amount);
        if (healed > 0) copy.currentHp += healed;
      }
    }
  };
  const all = (context.eventData as { allCombatCopies?: { ownCopies: Map<string, CombatMinionCopy>; opposingCopies: Map<string, CombatMinionCopy> } })?.allCombatCopies;
  if (all) {
    healFrom(all.ownCopies, true); // the holder's side — may over-feed
    healFrom(all.opposingCopies, false); // the enemy side — always capped
  } else {
    healFrom(context.combatCopies, true); // direct-dispatch fallback
  }
};

// ─── Inspired / Hero (task 207) — no-op executors (the passive
// convention) ─────────────────────────────────────────────────────────
// INSPIRED: the stats are applied by the RoundCoordinator's Hero grant
// path (it owns the session's inspiredStats map + the 'inspired' stream
// event). HERO: the grant fires in the coordinator's OnKill phase
// (attacking kills only — the coordinator's killerWasAttacker gate),
// where it has the kill attribution + both the killer's side copies and
// the combat event handler.
const inspiredExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Inspired section above (RoundCoordinator._grantInspired).
};

const heroExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Hero section above (the RoundCoordinator's OnKill phase
  // grants Inspired to the friendly side — the coordinator streams the
  // 'inspired' event with the per-target new stats).
};

const shieldBreakerExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Shield Breaker section above (the RoundCoordinator's
  // attack loop breaks the target's Shield on attack — the coordinator
  // streams the 'shieldBroken' event with the red shield-shatter FX).
};

// ─── Pyrotechnic (Mage) — combat damage aura (no-op executor, task 198) ──
// While a Pyrotechnic holder is ALIVE and in play, the Explosive and
// Exploding blasts of ALL of the owner's minions deal +PYROTECHNIC_BONUS_DAMAGE
// each (the holders STACK — two living Pyrotechnic minions = +6, the
// Warband per-copy stacking convention). The count is read LIVE at blast
// time in the two blast sites: the explosiveExecutor (this file —
// allCombatCopies.ownCopies is the blast owner's side) and
// RoundCoordinator._explodeExplodingCopy (the session copies of the blast
// owner's side). A Pyrotechnic minion that DIED earlier in the combat no
// longer counts, and the DYING Pyrotechnic itself never counts for its
// own blast (isAlive is false when the OnDeath window / the Exploding
// trigger run). The bonus is part of the
// ability damage (a Fortress victim doubles it with the rest of the
// blast). This executor is a no-op registered for completeness (the
// Strengthen / Exploding convention — the aura has no trigger event).
const PYROTECHNIC_ID = PYROTECHNIC_ABILITY_ID;

const pyrotechnicExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Pyrotechnic section above.
};

// ─── Spell-granted keywords (Task: New Spells) — no-op executors ───────────
// (The passive convention — the effects live in the combat engine: Treasure
// Hunter in the RoundCoordinator's Phase-3 kill detection + post-combat
// spawn, Divine Blessing in CombatMinionCopy.takeDamage, Infested in the
// RoundCoordinator's arming + post-combat removal. See AbilityConstants.)
const treasureHunterExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Treasure Hunter section above.
};

const divineBlessingExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Divine Blessing section above.
};

const infestedExecutor: AbilityExecutor = (_context: AbilityExecutionContext) => {
  // No-op: see the Infested section above.
};

// ─── Spellburst / SpellBlast (Spellmaker / Runekeeper) ────────────────────
// OnDeath: the owner receives a random spell in their hand (Spellburst:
// level 1 only; SpellBlast: any level — the full catalogue). The granted
// spell is a REAL spell card (cast, sell, give — the full lifecycle),
// built like a drawn card: a deterministic seed-derived instance id (the
// CardDrawService convention — 16 seeded bytes, UUID-like) and the
// catalogue's MinionDefinition. A full hand loses the card (the draw
// convention — addCard returns false). The roll + id use the match's
// seeded RNG (context.game.rng) so runs stay deterministic per seed.
function grantRandomSpellOnDeath(pool: readonly MinionDefinition[], idPrefix: string): AbilityExecutor {
  return (context: AbilityExecutionContext) => {
    if (pool.length === 0) return;
    const owner = context.owner;
    const def = pool[context.game.rng.nextInt(0, pool.length)];
    const card = getSpellMinionDefinition(def.id);
    if (!card) return;
    const hex = Array.from({ length: 16 }, () =>
      context.game.rng.nextInt(0, 256).toString(16).padStart(2, '0'),
    ).join('');
    const instance = new MinionInstance(
      `${idPrefix}_${hex}`,
      card.id,
      card.startingHp,
      card.attack,
      0,
      'base',
      card.baseAbilities,
      card.minionType,
    );
    owner.addCard(instance); // a full hand loses the card (the draw convention)
  };
}

const spellburstExecutor: AbilityExecutor = grantRandomSpellOnDeath(
  getSpellDefinitionsByLevel(1),
  'spellburst',
);
const spellblastExecutor: AbilityExecutor = grantRandomSpellOnDeath(
  // The FULL catalogue — every level that exists (1-3 since task 193's
  // Ban Hammer; derived from the catalogue so a future level is included
  // automatically).
  getSpellDefinitionsUpToLevel(Math.max(...SPELL_DEFINITIONS.map((s) => s.level))),
  'spellblast',
);

// ─── Registration Helper ───────────────────────────────────────────────────

/**
 * Registers all game abilities with the given AbilityDispatcher.
 * Call this once at server startup.
 */
export function registerAllAbilities(dispatcher: AbilityDispatcher): void {
  dispatcher.registerMany([
    { definition: GAME_ABILITY_DEFINITIONS[0], executor: lifestealExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[1], executor: shieldExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[2], executor: explosiveExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[3], executor: killerExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[4], executor: healExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[5], executor: buffVitalityExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[6], executor: drawExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[7], executor: resurrectExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[8], executor: fortressExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[9], executor: blastExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[10], executor: warbandExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[11], executor: necromancyExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[12], executor: improvedExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[13], executor: gravediggerExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[14], executor: discountExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[15], executor: heirloomExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[16], executor: teacherExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[17], executor: greaterHealExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[18], executor: buffAttackExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[19], executor: grandmasterExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[20], executor: deathfeederExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[21], executor: explodingExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[22], executor: soulLinkExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[23], executor: blessedExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[24], executor: masterBuffExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[25], executor: warTribeExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[26], executor: holyShieldExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[27], executor: secondChanceExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[28], executor: runeKeeperExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[29], executor: runeFeastExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[30], executor: toxicCloudExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[31], executor: bluntExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[32], executor: markExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[33], executor: sharedPainExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[34], executor: frenzyExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[35], executor: randomEvolutionExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[36], executor: strengthenExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[37], executor: blessingExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[38], executor: looterExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[39], executor: bruteExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[40], executor: freezeExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[41], executor: glasscannonExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[42], executor: swarmlordExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[43], executor: treasureExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[44], executor: lifeFullExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[45], executor: swarmnestExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[46], executor: spellmakerExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[47], executor: spellmasterExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[48], executor: treasureHunterExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[49], executor: divineBlessingExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[50], executor: infestedExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[51], executor: spellburstExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[52], executor: spellblastExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[53], executor: repeaterExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[54], executor: lastWishExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[55], executor: swiftExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[56], executor: pyrotechnicExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[57], executor: lifeBurstExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[58], executor: inspiredExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[59], executor: heroExecutor },
    { definition: GAME_ABILITY_DEFINITIONS[60], executor: shieldBreakerExecutor },
  ]);
}

// ─── Exported Ability IDs (for use in minion definitions) ──────────────────

export const ABILITY_IDS = {
  LIFESTEAL: LIFESTEAL_ID,
  SHIELD: SHIELD_ID,
  EXPLOSIVE: EXPLOSIVE_ID,
  KILLER: KILLER_ID,
  HEAL: HEAL_ID,
  BUFF_VITALITY: BUFF_VITALITY_ID,
  DRAW: DRAW_ID,
  RESURRECT: RESURRECT_ID,
  FORTRESS: FORTRESS_ID,
  BLAST: BLAST_ID,
  WARBAND: WARBAND_ID,
  NECROMANCY: NECROMANCY_ID,
  IMPROVED: IMPROVED_ID,
  GRAVEDIGGER: GRAVEDIGGER_ID,
  DISCOUNT: DISCOUNT_ID,
  HEIRLOOM: HEIRLOOM_ID,
  TEACHER: TEACHER_ID,
  GREATER_HEAL: GREATER_HEAL_ID,
  BUFF_ATTACK: BUFF_ATTACK_ID,
  GRANDMASTER: GRANDMASTER_ID,
  DEATHFEEDER: DEATHFEEDER_ID,
  EXPLODING: EXPLODING_ID,
  SOULLINK: SOULLINK_ID,
  BLESSED: BLESSED_ID,
  MASTER_BUFF: MASTER_BUFF_ID,
  WARTRIBE: WARTRIBE_ID,
  HOLY_SHIELD: HOLY_SHIELD_ID,
  SECOND_CHANCE: SECOND_CHANCE_ID,
  RUNE_KEEPER: RUNE_KEEPER_ID,
  RUNE_FEAST: RUNE_FEAST_ID,
  TOXIC_CLOUD: TOXIC_CLOUD_ID,
  BLUNT: BLUNT_ID,
  MARK: MARK_ID,
  SHARED_PAIN: SHARED_PAIN_ID,
  FRENZY: FRENZY_ID,
  RANDOM_EVOLUTION: RANDOM_EVOLUTION_ID,
  STRENGTHEN: STRENGTHEN_ID,
  BLESSING: BLESSING_ID,
  LOOTER: LOOTER_ID,
  BRUTE: BRUTE_ID,
  PYROTECHNIC: PYROTECHNIC_ID,
  SHIELD_BREAKER: SHIELD_BREAKER_ABILITY_ID,
} as const;
